// @ts-nocheck -- compact historical wizard handlers are covered by browser regression.
import { api } from '../../api.js';
import { AuthSnapshot } from '../../auth.js';
import { apiUrl } from '../../config.js';
import { getProfessionalModuleAccess, isBusinessFeatureEnabled } from '../../core/moduleAccess.js';
import { escapeHtml, renderLayout } from '../../layout.js';
import { navigate } from '../../router.js';
import { defaultLatestProgressReference, deriveLitigationListFieldIdsFromPlan, LITIGATION_INTERNAL_NO_PART_LABELS, LITIGATION_NUMBERING_KIND_LABELS, LITIGATION_PLAN_STANDARD_FIELDS, LITIGATION_SHEET_REFERENCE_MODULE, litigationFieldFillGuide, litigationPlanFieldLabel, litigationPlanMappingSourceKind, litigationReferenceSourceFieldId, litigationReferableFieldsForSheetKind, mergeLitigationSheetExportFieldsWithCatalog, LITIGATION_ORGANIZATION_LABEL_KIND_LABELS, normalizeLitigationInternalNoRule, normalizeLitigationPlanCollaboration, parseLitigationOrganizationLabelKind, previewLitigationInternalNo, suggestLitigationPlanField, type LitigationNumberingKind, type LitigationOrganizationLabelKind, type LitigationPlanCollaborationUnit, type LitigationPlanTeamEditor } from '../../../common/litigationLedgerPlan.js';
import {
    detectLitigationSheetKind,
    LITIGATION_DEFAULT_SHEETS,
    LITIGATION_DETAIL_FIELDS,
    PLATFORM_LITIGATION_TEMPLATE_DATA_START_ROW,
    PLATFORM_LITIGATION_TEMPLATE_HEADER_ROW,
} from '../../../common/litigationLedgerSheets.js';
import {
    LITIGATION_PLANS_PAGE_PATH,
    litigationPlanEditorHref,
    litigationPlanEditorPlanId,
} from '../legal-compliance/litigationLedgerPlanPage.js';
type Field = {
    id: string;
    label: string;
    source: string;
    group?: string;
    example?: string;
    required?: boolean;
    moduleId?: string;
    moduleVersion?: string;
    calculationValueType?: 'number' | 'percent';
    aliases?: string[];
    sheetKind?: 'cases' | 'parties' | 'procedure' | 'progress';
};
type Column = {
    columnIndex: number;
    columnLetter: string;
    header: string;
    suggestedField: string;
    suggestedModuleId?: string;
    suggestedModuleVersion?: string;
    suggestedScore: number;
};
type Sheet = {
    name: string;
    suggestedHeaderRow: number;
    rowCount: number;
    sheetKind?: 'cases' | 'parties' | 'procedure' | 'progress';
    columns: Column[];
};
type Inspection = {
    fileId?: string;
    fileName: string;
    activeSheetName?: string;
    sheets: Sheet[];
    standardFields: Field[];
};
type PlanField = {
    enabled: boolean;
    columnIndex?: number;
    columnLetter?: string;
    header?: string;
    sourceField?: string;
    sourceModuleId?: string;
    sourceModuleVersion?: string;
    valueMode?: string;
    referenceSheetName?: string;
    referenceColumnIndex?: number;
    referenceColumnLetter?: string;
    referenceHeader?: string;
    valueTransform?: string;
    transformLength?: number;
    transformText?: string;
    calculationExpression?: string;
    calculationDecimalPlaces?: number;
    calculationMissingValuePolicy?: string;
};
type Plan = {
    id: string;
    version?: number;
    name: string;
    companyName: string;
    fileName: string;
    sheetName: string;
    templateFileId?: string;
    ledgerType?: string;
    planKind?: 'ledger' | 'legal-litigation';
    listFieldIds?: string[];
    recognitionModuleId?: string;
    capabilityModuleIds?: string[];
    moduleVersions?: Record<string, string>;
    headerRow?: number;
    dataStartRow?: number;
    fields: PlanField[];
    sheetMaps?: LitigationSheetMapDraft[];
    planScope?: 'personal' | 'enterprise';
    creatorDisplayName?: string;
    creatorOrganizationName?: string;
    creatorOrganizationShortName?: string;
    creatorOrganizationCode?: string;
    collaborationUnits?: LitigationPlanCollaborationUnit[];
    teamEditors?: LitigationPlanTeamEditor[];
    internalNoRule?: ReturnType<typeof normalizeLitigationInternalNoRule>;
    updatedAt: string;
};
type LitigationSheetMapDraft = {
    sheetKind: 'cases' | 'parties' | 'procedure' | 'progress';
    sheetName: string;
    headerRow: number;
    dataStartRow: number;
    exportFields: PlanField[];
};
type Draft = {
    name: string;
    companyName: string;
    ledgerType: string;
    recognitionModuleId: string;
    capabilityModuleIds: string[];
    moduleVersions?: Record<string, string>;
    headerRow?: number;
    dataStartRow?: number;
    listFieldIds?: string[];
    planScope?: 'personal' | 'enterprise';
    creatorDisplayName?: string;
    creatorOrganizationName?: string;
    creatorOrganizationShortName?: string;
    creatorOrganizationCode?: string;
    collaborationUnits?: LitigationPlanCollaborationUnit[];
    teamEditors?: LitigationPlanTeamEditor[];
    internalNoRule?: ReturnType<typeof normalizeLitigationInternalNoRule>;
};
type PlanModule = [
    string,
    string
] & {
    id: string;
    version: string;
    name: string;
    description: string;
    category: string;
    runtime: string;
    source?: string;
    required: boolean;
    defaultSelected?: boolean;
    standardFields?: Field[];
};
const fieldKey = (field: Pick<Field, 'id'|'moduleId'|'moduleVersion'>): string => `${field.moduleId || ''}@${field.moduleVersion || ''}::${field.id}`;
const isInvoiceLedgerPlan = (draft: Draft | null = activePlanDraft): boolean => draft?.ledgerType === 'invoice-ledger';
const isLitigationLedgerPlan = (draft: Draft | null = activePlanDraft): boolean => draft?.ledgerType === 'legal-litigation';
/** 用户自定义内部案号在映射下拉里的取值。 */
const CUSTOM_INTERNAL_NO_MODULE = 'legal-litigation-custom-no';
const MANUAL_FILL_MODULE = 'legal-litigation-manual-fill-v1';
/** 诉讼方案「用户选择」类字段按标准字段 ID 分模块，避免下拉只显示一项或绑错列。 */
const litigationChoiceModuleId = (fieldId: string): string => `legal-litigation-choice-${fieldId}-v1`;
/** 诉讼跨表引用在映射下拉中的取值前缀。 */
const LITIGATION_REF_PREFIX = '__lit_ref__:';
/** 诉讼方案级联一级分类：引用其他表。 */
const LITIGATION_REF_GROUP = '引用其他表格数据';
/**
 * 编码跨表引用选项值。
 * @param sheetName 被引用工作表名
 * @param fieldId 被引用标准字段 ID
 */
function encodeLitigationSheetRef(sheetName: string, fieldId: string): string {
    return `${LITIGATION_REF_PREFIX}${sheetName}::${fieldId}`;
}
/**
 * 解析跨表引用选项值。
 * @param value 下拉选项值
 */
function decodeLitigationSheetRef(value: string): {sheetName: string; fieldId: string} | null {
    if (!value.startsWith(LITIGATION_REF_PREFIX)) return null;
    const body = value.slice(LITIGATION_REF_PREFIX.length);
    const split = body.indexOf('::');
    if (split <= 0) return null;
    return {sheetName: body.slice(0, split), fieldId: body.slice(split + 2)};
}
/**
 * 跨表引用项在级联/触发器上的展示文案。
 * @param value 编码后的选项值
 */
function litigationReferenceOptionLabel(value: string): string {
    const parsed = decodeLitigationSheetRef(value);
    if (!parsed) return value;
    const kind = detectLitigationSheetKind(parsed.sheetName);
    const label = litigationReferableFieldsForSheetKind(kind).find((item) => item.id === parsed.fieldId)?.label
        || litigationPlanFieldLabel(parsed.fieldId);
    return `${parsed.sheetName}｜${label}`;
}
/**
 * 当前编辑表之外、可被引用的工作表。
 * @param inspection 模板识别结果
 * @param currentSheetName 当前工作表名
 */
function litigationReferenceSheetOptions(inspection: Inspection | null, currentSheetName: string): Sheet[] {
    if (!inspection) return [];
    return inspection.sheets.filter((item) => item.name !== currentSheetName);
}
/** 打开内部案号规则弹窗的级联项，不写入映射。 */
const EDIT_INTERNAL_NO_RULE_VALUE = '__edit_internal_no_rule__';
/**
 * 当前方案下的自定义内部案号字段。
 * @param draft 方案草稿
 */
function customInternalNoField(draft: Draft | null = activePlanDraft): Field {
    const rule = normalizeLitigationInternalNoRule(draft?.internalNoRule);
    return {
        id: 'internalNo',
        label: previewPlanInternalNo(rule, draft),
        source: 'custom',
        sheetKind: 'cases',
        moduleId: CUSTOM_INTERNAL_NO_MODULE,
        moduleVersion: '1',
    };
}
/**
 * 规则示意里的单位代号：用方案管理员正在填写的单位代码。
 * @param draft 方案草稿
 */
function previewOrgCodeForDraft(draft: Draft | null | undefined = activePlanDraft): string {
    const input = document.querySelector<HTMLInputElement>('#lpAdminCode');
    if (input) return input.value.trim();
    return String(draft?.creatorOrganizationCode || '').trim();
}
/**
 * 按当前方案管理员的单位代码预览内部案号。
 * @param rule 编号规则
 * @param draft 方案草稿
 */
function previewPlanInternalNo(rule: unknown, draft: Draft | null | undefined = activePlanDraft): string {
    return previewLitigationInternalNo(normalizeLitigationInternalNoRule(rule), {orgCode: previewOrgCodeForDraft(draft)});
}
/** 当前会话是否允许把台账方案保存为团队方案。 */
const canUseTeamLedgerPlan = (auth: AuthSnapshot): boolean => auth.organization.organizationType === 'team';
const planFieldSources = (draft: Draft | null = activePlanDraft): Array<Field['source']> => {
    if (isLitigationLedgerPlan(draft)) return ['litigation', 'choice', 'manual', 'computed', 'custom'];
    return isInvoiceLedgerPlan(draft) ? ['invoice', 'computed'] : ['customs', 'refund', 'invoice', 'computed'];
};
const filterFieldsForPlan = (fields: Field[], draft: Draft | null = activePlanDraft): Field[] => {
    const allowed = new Set(planFieldSources(draft));
    return fields.filter(field => allowed.has(field.source));
};
function fieldSourceLabel(field:Field):string{
    if(field.source==='customs')return '报关单';
    if(field.source==='invoice')return '发票';
    if(field.source==='refund')return '商品退税率资料库';
    if(field.source==='computed')return '系统生成';
    if(field.source==='litigation')return '诉讼台账';
    if(field.source==='custom')return '用户自定义规则';
    if(field.source==='manual')return '用户手动填写';
    if(field.source==='choice')return '用户选择';
    if(field.source==='reference')return LITIGATION_REF_GROUP;
    return '其他来源';
}
function fieldSourceShortLabel(field:Field):string{
    if(field.source==='refund')return '退税率库';
    if(field.source==='invoice')return '发票';
    if(field.source==='computed')return '系统';
    if(field.source==='custom')return '规则';
    if(field.source==='choice')return '选择';
    if(field.source==='manual')return '手填';
    if(field.source==='litigation' || field.source==='manual'){
        if(field.sheetKind==='parties')return '案件背景';
        if(field.sheetKind==='procedure')return '程序';
        if(field.sheetKind==='progress')return '进展';
        return '总表';
    }
    return field.source==='customs'?'报关单':'其他';
}
/**
 * 四表类型中文说明。
 * @param kind 表类型
 */
function litigationSheetKindCaption(kind: Field['sheetKind'] | string | undefined): string {
    if (kind === 'parties') return '案件背景';
    if (kind === 'procedure') return '程序与期限';
    if (kind === 'progress') return '进展记录';
    return '案件总表';
}
function fieldProviderLabel(field:Field):string{
    const module=capabilities.find(item=>item.id===field.moduleId);
    const name=module?.name||fieldSourceLabel(field);
    const version=field.moduleVersion||module?.version||'';
    return `${name}${version?` v${version}`:''}`;
}
function fieldDisplayLabel(field:Field):string{
    if(field.source==='computed'&&field.id==='序号')return '连续序号（系统自动生成）';
    if(field.source==='custom'&&field.id==='internalNo')return previewPlanInternalNo(activePlanDraft?.internalNoRule);
    return field.label||field.id;
}
/**
 * 从映射键取出标准字段 ID。
 * @param key 模块字段键
 */
function fieldIdFromMappingKey(key: string): string {
    return key.includes('::') ? key.slice(key.lastIndexOf('::') + 2) : key;
}
/**
 * 诉讼映射下拉的填写提示。
 * @param key 模块字段键
 */
function litigationCascadeHint(key: string): string {
    if (key === EDIT_INTERNAL_NO_RULE_VALUE) return '点击后打开内部案号规则，按年份、案件类型等部分自行编排。';
    const id = fieldIdFromMappingKey(key);
    if (id === '序号') return '由通用字段处理模块按当前可见行自动编号，Excel 可留空。';
    const guide = litigationFieldFillGuide(id, isLitigationLedgerPlan() ? activePlanDraft?.internalNoRule : undefined);
    if (!guide) return '';
    return `${guide.rule}${guide.allowed ? ` 可选值：${guide.allowed}` : ''}${guide.example ? ` 示例：${guide.example}` : ''}`;
}
/**
 * 诉讼方案「内部案号规则」表单。
 * @param draft 当前草稿
 */
function litigationInternalNoRuleSection(draft: Draft): string {
    const rule = normalizeLitigationInternalNoRule(draft.internalNoRule);
    const partMeta = (kind: string): string => {
        if (kind === 'year') return '四位';
        if (kind === 'month') return '两位';
        if (kind === 'day') return '两位';
        if (kind === 'caseType') return '类型码';
        if (kind === 'serial') return '递增';
        return '';
    };
    const partRows = rule.parts.map((part, index) => {
        const orgCodeText = previewOrgCodeForDraft(draft);
        const extra = part.kind === 'orgCode'
            ? `<span class="ll-ino-part-meta">${escapeHtml(orgCodeText || '取方案里的单位代码')}</span>`
            : part.kind === 'other'
                ? `<input class="ll-ino-input" name="internalNoOther" maxlength="20" value="${escapeHtml(rule.otherText)}" placeholder="固定文字">`
                : `<span class="ll-ino-part-meta">${partMeta(part.kind)}</span>`;
        return `<li class="ll-ino-part" data-no-part="${part.kind}">
          <label class="ll-ino-part-check"><input type="checkbox" name="internalNoPartEnabled" ${part.enabled ? 'checked' : ''} ${part.kind === 'serial' ? 'disabled checked' : ''}><span>${escapeHtml(LITIGATION_INTERNAL_NO_PART_LABELS[part.kind])}</span></label>
          <div class="ll-ino-part-extra">${extra}</div>
          <div class="ll-ino-part-order">
            <button type="button" class="ll-ino-move" data-move="up" ${index === 0 ? 'disabled' : ''} aria-label="上移">↑</button>
            <button type="button" class="ll-ino-move" data-move="down" ${index === rule.parts.length - 1 ? 'disabled' : ''} aria-label="下移">↓</button>
          </div>
        </li>`;
    }).join('');
    const typeCodes = (Object.keys(LITIGATION_NUMBERING_KIND_LABELS) as LitigationNumberingKind[]).map((kind) =>
        `<label class="ll-ino-type"><span>${escapeHtml(LITIGATION_NUMBERING_KIND_LABELS[kind])}</span><input class="ll-ino-input" name="internalNoType_${kind}" maxlength="12" value="${escapeHtml(rule.caseTypeCodes[kind])}"></label>`
    ).join('');
    return `<section class="ll-internal-no-rule" id="llInternalNoRule">
      <h3>内部案号规则</h3>
      <p class="ll-internal-no-lead">勾选组成段并调整顺序。所属单位代号使用台账方案里的单位代码：方案管理员用自己的代码，协作单位用该单位的代码。</p>
      <div class="ll-ino-preview-bar">
        <span class="ll-ino-preview-label">示例</span>
        <code class="ll-ino-preview-value">${escapeHtml(previewPlanInternalNo(rule, draft))}</code>
      </div>
      <ul class="ll-ino-parts">${partRows}</ul>
      <div class="ll-ino-settings">
        <label class="ll-ino-setting"><span>分隔符</span><input class="ll-ino-input ll-ino-input-sm" name="internalNoSeparator" maxlength="3" value="${escapeHtml(rule.separator)}" placeholder="-"></label>
        <label class="ll-ino-setting"><span>流水位数</span><input class="ll-ino-input ll-ino-input-sm" name="internalNoSeqWidth" type="number" min="1" max="6" value="${rule.serialWidth}"></label>
        <label class="ll-ino-setting ll-ino-setting-wide"><span>备注</span><input class="ll-ino-input" name="internalNoNote" maxlength="200" value="${escapeHtml(rule.note)}" placeholder="选填"></label>
      </div>
      <div class="ll-ino-types">
        <span class="ll-ino-types-title">类型代号</span>
        <div class="ll-ino-types-grid">${typeCodes}</div>
      </div>
      <div class="ll-internal-no-preview" hidden aria-hidden="true"></div>
    </section>`;
}
/**
 * 从方案表单读取内部案号规则。
 * @param root 编辑页
 */
function readInternalNoRuleFromForm(root: HTMLElement): ReturnType<typeof normalizeLitigationInternalNoRule> {
    const box = root.querySelector<HTMLElement>('#llInternalNoRule');
    if (!box) return normalizeLitigationInternalNoRule(activePlanDraft?.internalNoRule);
    const parts = [...box.querySelectorAll<HTMLElement>('[data-no-part]')].map((row) => ({
        kind: String(row.dataset.noPart || ''),
        enabled: row.querySelector<HTMLInputElement>('input[name="internalNoPartEnabled"]')?.checked !== false,
    }));
    const typeCode = (kind: string) => String(box.querySelector<HTMLInputElement>(`[name="internalNoType_${kind}"]`)?.value || '');
    return normalizeLitigationInternalNoRule({
        parts,
        separator: String(box.querySelector<HTMLInputElement>('[name="internalNoSeparator"]')?.value ?? '-'),
        serialWidth: Number(box.querySelector<HTMLInputElement>('[name="internalNoSeqWidth"]')?.value || 3),
        orgCode: '',
        otherText: String(box.querySelector<HTMLInputElement>('[name="internalNoOther"]')?.value || ''),
        note: String(box.querySelector<HTMLInputElement>('[name="internalNoNote"]')?.value || ''),
        caseTypeCodes: {ss: typeCode('ss'), zx: typeCode('zx'), zc: typeCode('zc'), ldzc: typeCode('ldzc')},
    });
}
/**
 * 内部案号规则预览随输入更新，并支持调整组成段顺序。
 * @param root 编辑页
 * @param draft 草稿
 */
function bindInternalNoRulePreview(root: HTMLElement, draft: Draft): void {
    const box = root.querySelector<HTMLElement>('#llInternalNoRule');
    if (!box) return;
    const sync = () => {
        const rule = readInternalNoRuleFromForm(root);
        draft.internalNoRule = rule;
        const preview = box.querySelector('.ll-ino-preview-value');
        if (preview) preview.textContent = previewPlanInternalNo(rule, draft);
        const rows = [...box.querySelectorAll<HTMLElement>('[data-no-part]')];
        rows.forEach((row, index) => {
            const up = row.querySelector<HTMLButtonElement>('[data-move="up"]');
            const down = row.querySelector<HTMLButtonElement>('[data-move="down"]');
            if (up) up.disabled = index === 0;
            if (down) down.disabled = index === rows.length - 1;
        });
    };
    box.addEventListener('input', sync);
    box.addEventListener('change', sync);
    box.addEventListener('click', (event) => {
        const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-move]');
        if (!button || button.disabled) return;
        const row = button.closest<HTMLElement>('[data-no-part]');
        const list = row?.parentElement;
        if (!row || !list) return;
        if (button.dataset.move === 'up') {
            const previous = row.previousElementSibling;
            if (previous) list.insertBefore(row, previous);
        } else {
            const next = row.nextElementSibling;
            if (next) list.insertBefore(next, row);
        }
        sync();
    });
}
/**
 * 把方案草稿里的内部案号预览写回映射下拉。
 */
function refreshCustomInternalNoOptions(): void {
    const preview = previewPlanInternalNo(activePlanDraft?.internalNoRule);
    const key = fieldKey(customInternalNoField());
    document.querySelectorAll<HTMLSelectElement>('.mapping-table .map-source').forEach((select) => {
        const option = [...select.options].find((item) => item.value === key);
        if (option) option.textContent = `自定义｜${preview}`;
        select.dispatchEvent(new Event('change', {bubbles: true}));
    });
}
/**
 * 弹出内部案号规则编辑窗。确定后写回方案草稿，不立即保存方案。
 * @param afterSelect 选中自定义编号后的回调
 */
function openInternalNoRuleDialog(afterSelect?: () => void): void {
    const draft = activePlanDraft;
    if (!draft) return;
    document.getElementById('llInternalNoRuleDialog')?.remove();
    const snapshot = normalizeLitigationInternalNoRule(draft.internalNoRule);
    const overlay = document.createElement('dialog');
    overlay.id = 'llInternalNoRuleDialog';
    overlay.className = 'la-dialog ll-internal-no-dialog';
    overlay.innerHTML = `<header class="ll-ino-dialog-head"><h2>内部案号规则</h2><button class="la-dialog-close" type="button" data-close aria-label="关闭">×</button></header>
      <div class="la-dialog-body ll-ino-dialog-body">${litigationInternalNoRuleSection(draft)}</div>
      <footer class="ll-ino-dialog-foot">
        <button type="button" class="tp-btn-secondary" data-close>取消</button>
        <button type="button" class="tp-btn-primary" data-confirm>确定</button>
      </footer>`;
    document.body.appendChild(overlay);
    bindInternalNoRulePreview(overlay, draft);
    const previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    const close = (restore: boolean) => {
        document.documentElement.style.overflow = previousOverflow;
        if (restore) draft.internalNoRule = snapshot;
        overlay.close();
        overlay.remove();
        if (!restore) {
            refreshCustomInternalNoOptions();
            afterSelect?.();
        }
    };
    overlay.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => close(true)));
    overlay.querySelector('[data-confirm]')?.addEventListener('click', () => {
        draft.internalNoRule = readInternalNoRuleFromForm(overlay);
        close(false);
    });
    overlay.addEventListener('cancel', (event) => {
        event.preventDefault();
        close(true);
    });
    overlay.showModal();
}
function isCalculableField(field:Field):boolean{
    if(field.calculationValueType)return true;
    if(field.source!=='customs'&&field.source!=='invoice'&&field.source!=='litigation')return false;
    const label=field.label||field.id;
    return /件数|数量|重量|毛重|净重|单价|总价|金额|税率|税额|汇率|比例|费|标的/.test(label);
}
function formulaReferenceLabel(token:string, fields:Field[], inspection?:Inspection|null):string{
    const field=fields.find(item=>fieldKey(item)===token);
    if(field)return fieldDisplayLabel(field);
    if(inspection && token.includes('::')){
        const separator=token.lastIndexOf('::');
        const sheetName=token.slice(0,separator);
        const columnLetter=token.slice(separator+2);
        const column=inspection.sheets.find(sheet=>sheet.name===sheetName)?.columns.find(item=>item.columnLetter===columnLetter);
        if(column)return column.header||column.columnLetter;
    }
    const canonical=token.includes('::')?token.slice(token.lastIndexOf('::')+2):token;
    return canonical.includes('@')?canonical.slice(canonical.lastIndexOf('@')+1):canonical;
}
function formulaForDisplay(expression:string, fields:Field[], inspection?:Inspection|null):string{
    return expression.replace(/\[([^\]]+)\]/g, (_match, token:string) => {
        return `[${formulaReferenceLabel(token,fields,inspection)}]`;
    });
}
function formulaForStorage(expression:string, fields:Field[], inspection?:Inspection|null):string{
    return expression.replace(/\[([^\]]+)\]/g, (_match, token:string) => {
        // 同名工作表列不能覆盖专业字段。比如“退税率”应优先绑定商品退税率库，
        // 以字段别名录入时也需要转成稳定的模块字段引用。
        const matches=fields.filter(field=>fieldDisplayLabel(field)===token || field.label===token || field.id===token || field.aliases?.includes(token));
        if(matches.length===1)return `[${fieldKey(matches[0])}]`;
        const workbookMatches=(inspection?.sheets||[]).flatMap(sheet=>sheet.columns.filter(column=>column.header===token).map(column=>`${sheet.name}::${column.columnLetter}`));
        return workbookMatches.length===1 ? `[${workbookMatches[0]}]` : `[${token}]`;
    });
}
function selectedModuleFields(draft: Draft | null = activePlanDraft):Field[]{
    if(isLitigationLedgerPlan(draft)){
        const toField = (
            field: {id: string; label: string; aliases: string[]; sheetKind?: Field['sheetKind']},
            sheetKind: Field['sheetKind'],
            source: Field['source'],
        ): Field => ({
            id: field.id,
            label: field.label,
            source,
            aliases: field.aliases,
            sheetKind,
            moduleId: source === 'manual'
                ? MANUAL_FILL_MODULE
                : source === 'choice'
                    ? litigationChoiceModuleId(field.id)
                    : 'legal-litigation-ledger-v1',
            moduleVersion: '1',
            calculationValueType: field.id === 'amount' ? 'number' as const : undefined,
        });
        const mapLitigationField = (field: {id: string; label: string; aliases: string[]; sheetKind?: Field['sheetKind']}, sheetKind: Field['sheetKind']) => {
            const kind = litigationPlanMappingSourceKind(field.id);
            if (kind === 'computed' || kind === 'custom' || kind === 'reference') return null;
            const source: Field['source'] = kind === 'manual' ? 'manual' : kind === 'choice' ? 'choice' : 'litigation';
            return toField(field, sheetKind, source);
        };
        const litigationFields = [
            ...LITIGATION_PLAN_STANDARD_FIELDS.map((field) => mapLitigationField(field, 'cases')),
            ...LITIGATION_DETAIL_FIELDS.map((field) => mapLitigationField(field, field.sheetKind)),
        ].filter((field): field is Field => Boolean(field));
        const computedFields = systemFields.length ? systemFields : [{
            id:'序号',
            label:'连续序号',
            source:'computed',
            moduleId:'common-field-processing-v1',
            moduleVersion:'1.0.0',
        }];
        return filterFieldsForPlan([...litigationFields, ...computedFields, customInternalNoField(draft)], draft);
    }
    const fields=new Map<string,Field>();
    const allowedModules=new Set(ledgerCapabilityPayload(draft).capabilityModuleIds);
    capabilities
        .filter(item=>allowedModules.has(item.id))
        .flatMap(item=>(item.standardFields??[]).map(field=>({
            ...field,
            moduleId: field.moduleId || item.id,
            moduleVersion: field.moduleVersion || item.version,
        })))
        .forEach(field=>{
            if(field.moduleId&&field.moduleVersion)fields.set(fieldKey(field),field);
        });
    systemFields.forEach(field=>fields.set(fieldKey(field),field));
    return filterFieldsForPlan([...fields.values()], draft);
}
function planMappingFields(inspection?:Inspection|null, draft: Draft | null = activePlanDraft, sheetName?: string):Field[]{
    const merged=new Map<string,Field>();
    selectedModuleFields(draft).forEach(field=>merged.set(fieldKey(field),field));
    filterFieldsForPlan(inspection?.standardFields??[], draft).forEach(field=>{
        if(field.moduleId&&field.moduleVersion)merged.set(fieldKey(field),field);
    });
    const fields = [...merged.values()];
    if (!isLitigationLedgerPlan(draft)) return fields;
    const kind = detectLitigationSheetKind(sheetName || inspection?.activeSheetName || '');
    return fields.filter((field) => {
        if (field.source === 'computed' || field.source === 'custom' || field.source === 'manual' || field.source === 'choice') return true;
        const fieldKind = field.sheetKind || 'cases';
        if (kind === 'cases') return fieldKind === 'cases';
        return fieldKind === kind || field.id === 'internalNo';
    });
}
/**
 * 台账类型确定了可用的内置能力，发票台账方案不应暴露报关单识别和退税率库模块。
 */
function ledgerCapabilityPayload(draft: Draft | null = activePlanDraft):{capabilityModuleIds:string[];moduleVersions:Record<string,string>}{
    if(isLitigationLedgerPlan(draft)){
        return {capabilityModuleIds:[],moduleVersions:{}};
    }
    if(isInvoiceLedgerPlan(draft)){
        const capabilityModuleIds=[...new Set([
            draft?.recognitionModuleId||'invoice-unified-v1',
            'common-field-processing-v1',
            'ledger-export-v1',
            ...(draft?.capabilityModuleIds??[]),
        ])].filter(id=>{
            const module=capabilities.find(item=>item.id===id);
            return id!=='matching-v1'&&module?.category!=='customs-recognition'&&module?.category!=='refund-library';
        });
        return {
            capabilityModuleIds,
            moduleVersions:Object.fromEntries(capabilityModuleIds.map(id=>[id,capabilities.find(item=>item.id===id)?.version||draft?.moduleVersions?.[id]||'']).filter(([,version])=>Boolean(version))),
        };
    }
    const capabilityModuleIds=[...new Set(capabilities.map(item=>item.id).filter(Boolean))];
    return {
        capabilityModuleIds,
        moduleVersions:Object.fromEntries(capabilityModuleIds.map(id=>[id,capabilities.find(item=>item.id===id)?.version||'']).filter(([,version])=>Boolean(version))),
    };
}
function groupedFieldOptions(fields:Field[],selected='',includeFormula=true,draft: Draft | null = activePlanDraft):string{
    const eligible=filterFieldsForPlan(fields, draft);
    const order=planFieldSources(draft);
    const formula=includeFormula && !isLitigationLedgerPlan(draft)?`<option value="__formula__"${selected==='__formula__'?' selected':''}>计算｜自定义计算规则</option>`:'';
    const litigationRefOption = isLitigationLedgerPlan(draft) && selected.startsWith(LITIGATION_REF_PREFIX)
        ? `<optgroup label="${escapeHtml(LITIGATION_REF_GROUP)}"><option value="${escapeHtml(selected)}" selected>${escapeHtml(litigationReferenceOptionLabel(selected))}</option></optgroup>`
        : '';
    const groups=order.map(source=>{
        const items=eligible.filter(field=>field.source===source&&field.moduleId&&field.moduleVersion);
        if(!items.length && source!=='custom') return '';
        if(source==='custom' && isLitigationLedgerPlan(draft)){
            const previewField=customInternalNoField(draft);
            const previewKey=fieldKey(previewField);
            const previewSelected=selected===previewKey ? ' selected' : '';
            return `<optgroup label="用户自定义规则"><option value="${escapeHtml(previewKey)}"${previewSelected}>规则｜${escapeHtml(fieldDisplayLabel(previewField))}</option><option value="${EDIT_INTERNAL_NO_RULE_VALUE}">规则｜编辑内部案号规则</option></optgroup>`;
        }
        return items.length?`<optgroup label="${escapeHtml(fieldSourceLabel(items[0]))}">${items.map(field=>`<option value="${escapeHtml(fieldKey(field))}"${fieldKey(field)===selected?' selected':''}>${escapeHtml(fieldSourceShortLabel(field))}｜${escapeHtml(fieldDisplayLabel(field))}</option>`).join('')}</optgroup>`:'';
    }).join('');
    return `<option value="">— 不自动抓取 —</option>${formula}${groups}${litigationRefOption}`;
}
/**
 * 按标准字段 ID 解析诉讼映射下拉项（优先当前来源分类，兼容旧 moduleId）。
 * @param fieldId 标准字段 ID
 * @param fields 可选映射字段
 */
function litigationFieldKeyForId(fieldId: string, fields: Field[]): string {
    if (!fieldId) return '';
    if (fieldId === 'internalNo') {
        const custom = fields.find((field) => field.source === 'custom' && field.id === 'internalNo');
        if (custom) return fieldKey(custom);
    }
    const kind = litigationPlanMappingSourceKind(fieldId);
    const preferredSource = kind === 'choice' ? 'choice' : kind === 'litigation' ? 'litigation' : kind === 'manual' ? 'manual' : null;
    const provider = preferredSource
        ? fields.find((field) => field.id === fieldId && field.source === preferredSource)
            ?? fields.find((field) => field.id === fieldId)
        : fields.find((field) => field.id === fieldId);
    return provider ? fieldKey(provider) : '';
}
const mappingFieldKey = (field: PlanField | undefined, fields: Field[] = []): string => {
    if (!field?.sourceField) return '';
    if (isLitigationLedgerPlan()) {
        const preferred = litigationFieldKeyForId(field.sourceField, fields);
        if (preferred) return preferred;
    }
    const provider = fields.find(item => item.id === field.sourceField && (!field.sourceModuleId || item.moduleId === field.sourceModuleId) && (!field.sourceModuleVersion || item.moduleVersion === field.sourceModuleVersion)) ?? fields.find(item => item.id === field.sourceField);
    return provider ? fieldKey(provider) : '';
};
const suggestionFieldKey = (column: Column, fields: Field[], sheetKind: Field['sheetKind'] = 'cases'): string => {
    const suggested = column.suggestedField || (isLitigationLedgerPlan() ? suggestLitigationPlanField(column.header, sheetKind) : '');
    if (!suggested) return '';
    return litigationFieldKeyForId(suggested, fields);
};
/**
 * 诉讼方案：按 Excel 表头选定映射项，避免「审级」列绑到「案件类型」。
 * @param column 识别列
 * @param fields 映射字段
 * @param existing 已保存映射
 * @param sheetKind 工作表类型
 */
/**
 * 诉讼映射行：当前 Excel 列在指定来源分类下应对应的下拉键（手填/选择本列，无需再挑其它字段）。
 * @param row 映射表行
 * @param groupLabel 级联一级分类名
 */
function litigationRowScopedFieldKeyForGroup(row: HTMLElement | null, groupLabel: string): string {
    if (!row || !isLitigationLedgerPlan()) return '';
    const header = row.dataset.header || '';
    if (!header.trim()) return '';
    const sheetName = document.querySelector<HTMLSelectElement>('#sheetSelect')?.value;
    const kind = detectLitigationSheetKind(sheetName || activeInspection?.activeSheetName || '');
    const preferredId = suggestLitigationPlanField(header, kind);
    if (!preferredId) return '';
    const sourceWanted = groupLabel === '用户手动填写'
        ? 'manual'
        : groupLabel === '用户选择'
            ? 'choice'
            : '';
    if (!sourceWanted || litigationPlanMappingSourceKind(preferredId) !== sourceWanted) return '';
    const fields = planMappingFields(activeInspection, activePlanDraft, sheetName);
    return litigationFieldKeyForId(preferredId, fields);
}
function litigationMappingSelectValue(
    column: Column,
    fields: Field[],
    existing: PlanField | undefined,
    sheetKind: Field['sheetKind'],
): string {
    const preferredId = isLitigationLedgerPlan() ? suggestLitigationPlanField(column.header, sheetKind) : '';
    if (existing?.valueMode === 'reference' && existing.referenceSheetName) {
        const refKind = detectLitigationSheetKind(existing.referenceSheetName);
        const refFieldId = litigationReferenceSourceFieldId(existing.referenceHeader || '', refKind)
            || (existing.sourceField === 'latestProgress' ? 'progressContent' : '');
        if (refFieldId) return encodeLitigationSheetRef(existing.referenceSheetName, refFieldId);
    }
    if (preferredId === 'latestProgress' && sheetKind === 'cases') {
        const progressSheet = activeInspection?.sheets.find((item) => detectLitigationSheetKind(item.name) === 'progress');
        const def = defaultLatestProgressReference();
        return encodeLitigationSheetRef(progressSheet?.name || def.referenceSheetName, 'progressContent');
    }
    if (existing?.sourceField) {
        let key = mappingFieldKey(existing, fields);
        if (preferredId && key && fieldIdFromMappingKey(key) !== preferredId) {
            const headerNorm = column.header.trim();
            const standard = LITIGATION_PLAN_STANDARD_FIELDS.find((item) => item.id === preferredId);
            const aliasHit = standard && [standard.label, ...standard.aliases].some((alias) => alias.trim() === headerNorm);
            if (aliasHit) {
                const corrected = litigationFieldKeyForId(preferredId, fields);
                if (corrected) key = corrected;
            }
        }
        return key;
    }
    return suggestionFieldKey(column, fields, sheetKind);
};
function bestInspectionSheet(sheets: Sheet[], activeSheetName = ''): Sheet | undefined {
    if (isLitigationLedgerPlan()) {
        return sheets.find(sheet => sheet.name === activeSheetName)
            ?? sheets.find(sheet => (sheet.sheetKind || detectLitigationSheetKind(sheet.name)) === 'cases')
            ?? sheets.find(sheet => sheet.columns.length > 1)
            ?? sheets[0];
    }
    return sheets.find(sheet => sheet.name === activeSheetName && sheet.columns.length > 1)
        ?? sheets.find(sheet => sheet.columns.length > 1)
        ?? sheets.find(sheet => sheet.columns.length > 0)
        ?? sheets[0];
}
let capabilities: PlanModule[] = [];
let systemFields: Field[] = [];
let defaultRecognitionModuleId = 'customs-declaration-unified-v1';
let activePlanDraft: Draft | null = null;
let activePlanAuth: AuthSnapshot | null = null;
let activeMappingFields: PlanField[] = [];
let activeInspection: Inspection | null = null;
/** 诉讼台账四表映射草稿，切换工作表时保留。 */
const litigationSheetMaps = new Map<string, LitigationSheetMapDraft>();
/** 报关单/发票台账方案：各工作表表头行与数据起始行。 */
const ledgerSheetStructureDraft = new Map<string, {headerRow: number; dataStartRow: number}>();
/**
 * 清空诉讼四表映射草稿。
 */
function resetLitigationSheetMaps(): void {
    litigationSheetMaps.clear();
}
/** 清空报关单/发票方案的工作表行号草稿。 */
function resetLedgerSheetStructureDraft(): void {
    ledgerSheetStructureDraft.clear();
}
/**
 * 按当前工作表识别四表类型。
 * @param sheet 识别结果
 */
function sheetKindOf(sheet: Pick<Sheet, 'name' | 'sheetKind'> | undefined): Field['sheetKind'] {
    return sheet?.sheetKind || detectLitigationSheetKind(sheet?.name || '');
}
/**
 * 用识别结果补全尚未保存的工作表映射。
 * @param inspection 识别结果
 * @param draft 方案草稿
 */
/**
 * 由识别列生成默认导出字段映射。
 * @param column 识别列
 * @param kind 工作表类型
 * @param mappingFields 可选标准字段
 */
function exportFieldFromInspectedColumn(
    column: Sheet['columns'][number],
    kind: Field['sheetKind'],
    mappingFields: Field[],
    inspection?: Inspection | null,
): PlanField {
    const suggested = column.suggestedField || suggestLitigationPlanField(column.header, kind);
    if (suggested === 'latestProgress' && kind === 'cases') {
        const def = defaultLatestProgressReference();
        const progressSheet = inspection?.sheets.find((item) => detectLitigationSheetKind(item.name) === 'progress');
        const sheetName = progressSheet?.name || def.referenceSheetName;
        const contentCol = progressSheet?.columns.find((item) => suggestLitigationPlanField(item.header, 'progress') === 'progressContent');
        return {
            enabled: true,
            columnIndex: column.columnIndex,
            columnLetter: column.columnLetter,
            header: column.header,
            sourceField: 'latestProgress',
            sourceModuleId: LITIGATION_SHEET_REFERENCE_MODULE,
            sourceModuleVersion: '1',
            valueMode: 'reference',
            referenceSheetName: sheetName,
            referenceColumnIndex: contentCol?.columnIndex,
            referenceColumnLetter: contentCol?.columnLetter,
            referenceHeader: contentCol?.header || def.referenceHeader,
        };
    }
    const field = suggested === 'internalNo'
        ? mappingFields.find((item) => item.source === 'custom' && item.id === 'internalNo') || mappingFields.find((item) => item.id === suggested)
        : (() => {
            const key = litigationFieldKeyForId(suggested, mappingFields);
            return key ? mappingFields.find((item) => fieldKey(item) === key) : mappingFields.find((item) => item.id === suggested);
        })();
    return {
        enabled: Boolean(field),
        columnIndex: column.columnIndex,
        columnLetter: column.columnLetter,
        header: column.header,
        sourceField: field?.id || '',
        sourceModuleId: field?.moduleId,
        sourceModuleVersion: field?.moduleVersion,
        valueMode: 'source',
    };
}
/** 诉讼台账方案使用的固定表结构行号（与后台标准四表模板一致）。 */
function fixedLitigationSheetStructure(): {headerRow: number; dataStartRow: number} {
    return {
        headerRow: PLATFORM_LITIGATION_TEMPLATE_HEADER_ROW,
        dataStartRow: PLATFORM_LITIGATION_TEMPLATE_DATA_START_ROW,
    };
}

function seedLitigationSheetMapsFromInspection(inspection: Inspection, draft: Draft): void {
    if (!isLitigationLedgerPlan(draft)) return;
    const structure = fixedLitigationSheetStructure();
    for (const sheet of inspection.sheets) {
        const kind = sheetKindOf(sheet);
        const mappingFields = planMappingFields(inspection, draft, sheet.name);
        const existing = litigationSheetMaps.get(sheet.name);
        const priorFields = existing?.exportFields?.length
            ? existing.exportFields
            : sheet.columns.map((column) => exportFieldFromInspectedColumn(column, kind, mappingFields, inspection));
        const exportFields = mergeLitigationSheetExportFieldsWithCatalog(kind, priorFields, sheet.columns) as PlanField[];
        litigationSheetMaps.set(sheet.name, {
            sheetKind: kind,
            sheetName: sheet.name,
            headerRow: structure.headerRow,
            dataStartRow: structure.dataStartRow,
            exportFields,
        });
    }
}
/**
 * 确认表头行后刷新当前工作表映射，尽量保留同列字母上的已有配置。
 * @param sheetName 工作表名
 * @param inspection 识别结果
 * @param draft 方案草稿
 * @param previousFields 确认前的映射
 * @param headerRow 表头行
 * @param dataStartRow 数据起始行
 */
function applyLitigationSheetStructureAfterReinspect(
    sheetName: string,
    inspection: Inspection,
    draft: Draft,
    previousFields: PlanField[],
    headerRow: number,
    dataStartRow: number,
): void {
    const sheet = inspection.sheets.find((item) => item.name === sheetName);
    if (!sheet) return;
    const kind = sheetKindOf(sheet);
    const mappingFields = planMappingFields(inspection, draft, sheetName);
    const byLetter = new Map(previousFields.map((field) => [field.columnLetter, field]));
    litigationSheetMaps.set(sheetName, {
        sheetKind: kind,
        sheetName,
        headerRow,
        dataStartRow,
        exportFields: sheet.columns.map((column) => {
            const kept = byLetter.get(column.columnLetter);
            if (kept) {
                return {...kept, header: column.header, columnIndex: column.columnIndex};
            }
            return exportFieldFromInspectedColumn(column, kind, mappingFields, activeInspection);
        }),
    });
}
/**
 * 由识别列生成报关单/发票方案默认字段映射。
 * @param column 识别列
 * @param mappingFields 可选标准字段
 */
function exportFieldFromLedgerColumn(column: Column, mappingFields: Field[]): PlanField {
    const sourceKey = suggestionFieldKey(column, mappingFields);
    const selectedField = mappingFields.find((field) => fieldKey(field) === sourceKey);
    return {
        enabled: Boolean(sourceKey),
        columnIndex: column.columnIndex,
        columnLetter: column.columnLetter,
        header: column.header,
        sourceField: selectedField?.id || column.suggestedField || '',
        sourceModuleId: selectedField?.moduleId || column.suggestedModuleId || '',
        sourceModuleVersion: selectedField?.moduleVersion || column.suggestedModuleVersion || '',
        valueMode: 'source',
    };
}
/**
 * 确认表头行后刷新报关单/发票当前工作表映射。
 * @param sheetName 工作表名
 * @param inspection 识别结果
 * @param draft 方案草稿
 * @param previousFields 确认前的映射
 * @param headerRow 表头行
 * @param dataStartRow 数据起始行
 */
function applyLedgerSheetStructureAfterReinspect(
    sheetName: string,
    inspection: Inspection,
    draft: Draft,
    previousFields: PlanField[],
    headerRow: number,
    dataStartRow: number,
): PlanField[] {
    ledgerSheetStructureDraft.set(sheetName, {headerRow, dataStartRow});
    const sheet = inspection.sheets.find((item) => item.name === sheetName);
    if (!sheet) return previousFields;
    const mappingFields = planMappingFields(inspection, draft, sheetName);
    const byLetter = new Map(previousFields.map((field) => [field.columnLetter, field]));
    return sheet.columns.map((column) => {
        const kept = byLetter.get(column.columnLetter);
        if (kept) {
            return {...kept, header: column.header, columnIndex: column.columnIndex};
        }
        return exportFieldFromLedgerColumn(column, mappingFields);
    });
}
/**
 * 读取当前映射表中的列配置。
 * @param root 编辑页根节点
 * @param inspection 识别结果
 */
function readEditorMappingRows(root: HTMLElement, inspection: Inspection | null): PlanField[] {
    return [...root.querySelectorAll<HTMLTableRowElement>('[data-map-row]')].map((row) => {
        const selectedValue = row.querySelector<HTMLSelectElement>('.map-source')?.value || '';
        const litRef = decodeLitigationSheetRef(selectedValue);
        const valueMode = selectedValue === '__formula__' ? 'formula' : litRef ? 'reference' : 'source';
        const referenceSheet = litRef?.sheetName || row.querySelector<HTMLSelectElement>('.map-reference-sheet')?.value || '';
        const referenceColumn = row.querySelector<HTMLSelectElement>('.map-reference-column');
        const referenceColumnIndex = litRef
            ? inspection?.sheets.find((sheet) => sheet.name === litRef.sheetName)?.columns.find((column) => {
                const kind = detectLitigationSheetKind(litRef.sheetName);
                return suggestLitigationPlanField(column.header, kind) === litRef.fieldId;
            })?.columnIndex
            : Number(referenceColumn?.value);
        const referenceColumnData = inspection?.sheets.find((sheet) => sheet.name === referenceSheet)?.columns.find((column) => Number(column.columnIndex) === Number(referenceColumnIndex));
        const resolvedValueMode = selectedValue === '__reference__' || litRef ? 'reference' : valueMode;
        const sheetName = document.querySelector<HTMLSelectElement>('#sheetSelect')?.value;
        const currentKind = detectLitigationSheetKind(sheetName || inspection?.activeSheetName || '');
        const availableFields = planMappingFields(inspection, activePlanDraft, sheetName);
        const selectedField = valueMode === 'source' ? availableFields.find((field) => fieldKey(field) === selectedValue) : undefined;
        const standardFieldId = row.dataset.standardFieldId || '';
        const sourceField = litRef
            ? (standardFieldId || suggestLitigationPlanField(row.dataset.header || '', currentKind) || selectedField?.id || '')
            : (selectedField?.id || standardFieldId || '');
        const calculationExpression = formulaForStorage(row.querySelector<HTMLTextAreaElement>('.map-calculation-expression')?.value.trim() || '', selectedModuleFields(), inspection);
        return {
            columnIndex: Number(row.dataset.columnIndex),
            columnLetter: row.dataset.columnLetter || '',
            header: row.dataset.header || '',
            enabled: Boolean(row.querySelector<HTMLInputElement>('.map-enabled')?.checked && (resolvedValueMode === 'formula' ? calculationExpression : resolvedValueMode === 'reference' ? referenceSheet && Number.isInteger(referenceColumnIndex) && referenceColumnIndex >= 0 : sourceField)),
            sourceField,
            sourceModuleId: litRef ? LITIGATION_SHEET_REFERENCE_MODULE : (selectedField?.moduleId || ''),
            sourceModuleVersion: litRef ? '1' : (selectedField?.moduleVersion || ''),
            valueMode: resolvedValueMode,
            referenceSheetName: referenceSheet,
            referenceColumnIndex: Number.isInteger(Number(referenceColumnIndex)) && Number(referenceColumnIndex) >= 0 ? Number(referenceColumnIndex) : undefined,
            referenceColumnLetter: referenceColumnData?.columnLetter || '',
            referenceHeader: litRef
                ? (litigationReferableFieldsForSheetKind(detectLitigationSheetKind(litRef.sheetName)).find((item) => item.id === litRef.fieldId)?.label || referenceColumnData?.header || '')
                : (referenceColumnData?.header || ''),
            valueTransform: row.querySelector<HTMLSelectElement>('.map-transform')?.value || '',
            transformLength: Number(row.querySelector<HTMLInputElement>('.map-transform-length')?.value) || 3,
            transformText: row.querySelector<HTMLInputElement>('.map-transform-text')?.value || '',
            calculationExpression,
            calculationDecimalPlaces: Number(row.querySelector<HTMLInputElement>('.map-calculation-decimals')?.value) || 0,
            calculationMissingValuePolicy: row.querySelector<HTMLSelectElement>('.map-calculation-missing')?.value || 'error',
        };
    });
}
/**
 * 把当前工作表的映射写入四表草稿。
 * @param root 编辑页根节点
 * @param sheetName 工作表名
 */
function harvestCurrentLitigationSheet(root: HTMLElement, sheetName: string): void {
    if (!isLitigationLedgerPlan() || !sheetName) return;
    const {headerRow, dataStartRow} = fixedLitigationSheetStructure();
    litigationSheetMaps.set(sheetName, {
        sheetKind: detectLitigationSheetKind(sheetName),
        sheetName,
        headerRow,
        dataStartRow,
        exportFields: readEditorMappingRows(root, activeInspection),
    });
}
/**
 * 保存报关单/发票方案当前工作表的行号与映射。
 * @param root 编辑页根节点
 * @param sheetName 工作表名
 */
function harvestCurrentLedgerPlanSheet(root: HTMLElement, sheetName: string): void {
    if (isLitigationLedgerPlan() || !sheetName) return;
    const structure = readStructureRowInputs(root, sheetName);
    if (structure) {
        ledgerSheetStructureDraft.set(sheetName, {headerRow: structure.headerRow, dataStartRow: structure.dataStartRow});
    }
    activeMappingFields = readEditorMappingRows(root, activeInspection);
}
/**
 * 保存当前工作表草稿（诉讼或报关单/发票）。
 * @param root 编辑页根节点
 * @param sheetName 工作表名
 */
function harvestCurrentPlanSheet(root: HTMLElement, sheetName: string): void {
    if (isLitigationLedgerPlan()) harvestCurrentLitigationSheet(root, sheetName);
    else harvestCurrentLedgerPlanSheet(root, sheetName);
}
/** 读取已确认的表头行（未确认时用识别建议行）。 */
function resolvedPlanHeaderRow(currentSheet: string, activeSheet: Sheet | undefined): number | undefined {
    if (isLitigationLedgerPlan()) {
        return fixedLitigationSheetStructure().headerRow;
    }
    return ledgerSheetStructureDraft.get(currentSheet)?.headerRow ?? activeSheet?.suggestedHeaderRow;
}
/** 新建向导返回方案列表时恢复原来的筛选地址。 */
let setupReturnHref = '/tax-assistant/plans';
function ensureEditorModuleOptions(): void {
    document.querySelector('.plan-editor-capabilities')?.remove();

    const structure = document.querySelector<HTMLElement>('#planEditor .template-structure-grid');
    if (!structure || structure.dataset.refined === 'true') return;
    if (structure.classList.contains('template-structure-grid--litigation')) {
        structure.dataset.refined = 'true';
        return;
    }
    structure.dataset.refined = 'true';

    const labels = Array.from(structure.querySelectorAll<HTMLLabelElement>(':scope > label'));
    const sheetLabel = labels[0];
    const rowLabel = labels[1];
    const sheetSelect = sheetLabel?.querySelector<HTMLSelectElement>('#sheetSelect');
    const headerInput = rowLabel?.querySelector<HTMLInputElement>('input[name="headerRow"]');
    const dataStartInput = rowLabel?.querySelector<HTMLInputElement>('input[name="dataStartRow"]');
    if (!sheetLabel || !rowLabel || !sheetSelect || !headerInput || !dataStartInput) return;

    const requiredTitle = (text: string): HTMLSpanElement => {
        const title = document.createElement('span');
        title.className = 'form-label-line';
        title.append(document.createTextNode(text));
        const required = document.createElement('span');
        required.className = 'req';
        required.textContent = '*';
        title.append(required);
        return title;
    };

    const sheetCount = activeInspection?.sheets.length ?? 1;
    sheetLabel.className = 'template-structure-sheet';
    sheetLabel.replaceChildren(requiredTitle(`工作表（共 ${sheetCount} 个表）`), sheetSelect);

    rowLabel.className = 'template-structure-number';
    rowLabel.replaceChildren(requiredTitle('表头所在行'), headerInput);

    const dataStartLabel = document.createElement('label');
    dataStartLabel.className = 'template-structure-number';
    dataStartLabel.replaceChildren(requiredTitle('数据起始行'), dataStartInput);
    structure.append(dataStartLabel);

    const confirmWrap = document.createElement('div');
    confirmWrap.className = 'template-structure-confirm';
    const confirmButton = document.createElement('button');
    confirmButton.type = 'button';
    confirmButton.className = 'secondary-button';
    confirmButton.id = 'confirmStructureRows';
    confirmButton.textContent = '确定';
    confirmWrap.append(confirmButton);
    structure.append(confirmWrap);
}
/**
 * 读取结构区当前工作表与行号。
 * @param root 编辑页根节点
 * @param fallbackSheetName 默认工作表名
 */
function readStructureRowInputs(root: HTMLElement, fallbackSheetName: string): {
    sheetName: string;
    headerRow: number;
    dataStartRow: number;
} | null {
    const sheetName = root.querySelector<HTMLSelectElement>('#sheetSelect')?.value?.trim()
        || fallbackSheetName.trim();
    const headerRow = Math.floor(Number(root.querySelector<HTMLInputElement>('#planEditor input[name="headerRow"]')?.value));
    const dataStartRow = Math.floor(Number(root.querySelector<HTMLInputElement>('#planEditor input[name="dataStartRow"]')?.value));
    if (!sheetName || !Number.isFinite(headerRow) || headerRow < 1 || !Number.isFinite(dataStartRow) || dataStartRow < 1) {
        return null;
    }
    return {sheetName, headerRow, dataStartRow};
}
/**
 * 生成列名签名，用于判断重新识别后是否有变化。
 * @param columns 识别列
 */
function planColumnHeaderSignature(columns: Sheet['columns']): string {
    return columns.map((column) => `${column.columnLetter}:${column.header}`).join('|');
}
/**
 * 绑定「确定」表头行与数据起始行，重新识别列并刷新映射表。
 */
function bindStructureRowConfirm(
    root: HTMLElement,
    auth: AuthSnapshot,
    fileId: string,
    draft: Draft,
    editingId: string | null,
    sheetName: string,
): void {
    if (isLitigationLedgerPlan(draft)) return;
    const button = root.querySelector<HTMLButtonElement>('#confirmStructureRows');
    if (!button) return;
    button.onclick = () => {
        void (async () => {
            const message = root.querySelector<HTMLElement>('#editorMessage');
            const structure = readStructureRowInputs(root, sheetName);
            if (!structure) {
                if (message) message.textContent = '请填写有效的工作表、表头行与数据起始行';
                return;
            }
            const {sheetName: currentSheet, headerRow: header, dataStartRow: start} = structure;
            if (start <= header) {
                if (message) message.textContent = '数据起始行必须大于表头所在行';
                return;
            }
            const activeSheet = activeInspection?.sheets.find((item) => item.name === currentSheet);
            const previousHeaderRow = resolvedPlanHeaderRow(currentSheet, activeSheet);
            const previousSignature = activeSheet ? planColumnHeaderSignature(activeSheet.columns) : '';
            const previousFields = isLitigationLedgerPlan(draft)
                ? (litigationSheetMaps.get(currentSheet)?.exportFields || readEditorMappingRows(root, activeInspection))
                : readEditorMappingRows(root, activeInspection);
            harvestCurrentPlanSheet(root, currentSheet);
            button.disabled = true;
            if (message) message.textContent = '正在确认表头与数据行…';
            try {
                let nextInspection = activeInspection;
                if (!nextInspection) throw new Error('缺少模板识别结果，请重新上传模板');
                if (previousHeaderRow != null && header === previousHeaderRow) {
                    if (isLitigationLedgerPlan(draft)) {
                        litigationSheetMaps.set(currentSheet, {
                            ...(litigationSheetMaps.get(currentSheet) || {
                                sheetKind: sheetKindOf(activeSheet),
                                sheetName: currentSheet,
                                exportFields: previousFields,
                            }),
                            sheetKind: sheetKindOf(activeSheet),
                            sheetName: currentSheet,
                            headerRow: header,
                            dataStartRow: start,
                            exportFields: previousFields,
                        });
                    } else {
                        ledgerSheetStructureDraft.set(currentSheet, {headerRow: header, dataStartRow: start});
                    }
                    const statusOnlyDataStart = `数据起始行已设为第 ${start} 行。列名由「表头所在行」（当前第 ${header} 行）决定，如需变更列名请修改表头行后再点确定。`;
                    const fields = isLitigationLedgerPlan(draft)
                        ? (litigationSheetMaps.get(currentSheet)?.exportFields || previousFields)
                        : previousFields;
                    renderEditor(root, auth, fileId, nextInspection, draft, editingId, currentSheet, fields, statusOnlyDataStart);
                    return;
                }
                if (message) message.textContent = '正在按表头行重新识别列…';
                nextInspection = await inspectPlanFile(fileId, draft, {sheetName: currentSheet, headerRow: header});
                let nextFields: PlanField[];
                if (isLitigationLedgerPlan(draft)) {
                    applyLitigationSheetStructureAfterReinspect(currentSheet, nextInspection, draft, previousFields, header, start);
                    nextFields = litigationSheetMaps.get(currentSheet)?.exportFields || [];
                } else {
                    nextFields = applyLedgerSheetStructureAfterReinspect(currentSheet, nextInspection, draft, previousFields, header, start);
                }
                const nextSheet = nextInspection.sheets.find((item) => item.name === currentSheet);
                const nextSignature = nextSheet ? planColumnHeaderSignature(nextSheet.columns) : '';
                const statusMessage = nextSignature && nextSignature === previousSignature
                    ? `已确认第 ${header} 行，但列名与调整前相同，请检查该行是否包含列标题。`
                    : `已按第 ${header} 行读取 ${nextSheet?.columns.length ?? 0} 个列名，数据从第 ${start} 行起。`;
                renderEditor(root, auth, fileId, nextInspection, draft, editingId, currentSheet, nextFields, statusMessage);
            } catch (error) {
                if (message) message.textContent = error instanceof Error ? error.message : String(error);
            } finally {
                button.disabled = false;
            }
        })();
    };
}
function steps(_active: number): string { return ''; }
/** 地址栏 kind 用于默认筛到诉讼台账方案。 */
function requestedPlanKind(): string {
    return new URLSearchParams(window.location.search).get('kind') || '';
}
/** 是否具备诉讼台账方案能力。 */
function canManageLitigationPlans(): boolean {
    return getProfessionalModuleAccess()['legal-compliance-assistant'] && isBusinessFeatureEnabled('legal-litigation-ledger-v1');
}
/** 是否可管理报关单匹配台账方案。 */
function canManageCustomsPlans(): boolean {
    return getProfessionalModuleAccess()['finance-management'] && isBusinessFeatureEnabled('customs-invoice-matching-v1');
}
/** 是否可管理发票台账方案。 */
function canManageInvoicePlans(): boolean {
    return getProfessionalModuleAccess()['finance-management'] && isBusinessFeatureEnabled('invoice-ledger-v1');
}
/** 是否可加载报关/发票台账方案，不依赖当前选中任务。 */
function canManageFinancePlans(): boolean {
    return canManageCustomsPlans() || canManageInvoicePlans();
}
function asLitigationListPlan(plan: {id: string; name: string; companyName?: string; fileName?: string; sheetName?: string; templateFileId?: string; updatedAt: string}): Plan {
    return {
        id: plan.id,
        name: plan.name,
        companyName: plan.companyName || '',
        fileName: plan.fileName || '',
        sheetName: plan.sheetName || '诉讼台账',
        templateFileId: plan.templateFileId,
        fields: [],
        updatedAt: plan.updatedAt,
        ledgerType: 'legal-litigation',
        planKind: 'legal-litigation',
    };
}
function isLitigationListPlan(plan: Plan): boolean {
    return plan.planKind === 'legal-litigation' || plan.ledgerType === 'legal-litigation';
}
/**
 * 按台账类型识别模板。诉讼方案走独立识别接口。
 * @param fileId 模板文件 ID
 * @param draft 当前方案草稿
 */
function normalizeLitigationInspectPayload(
    payload: Inspection,
    fileId: string,
    draft: Draft,
    options: {preserveHeaderRow?: boolean; retainFullHeaders?: boolean} = {},
): Inspection {
    const preserveHeaderRow = Boolean(options.preserveHeaderRow);
    const retainFullHeaders = Boolean(options.retainFullHeaders);
    const sheets = (payload.sheets || []).map((sheet) => {
        const kind = sheet.sheetKind || detectLitigationSheetKind(sheet.name);
        const columns = (sheet.columns || []).map((column) => {
            const header = retainFullHeaders
                ? String(column.header || '').trim()
                : String(column.header || '').split(/\s*\/\s*/)[0].trim() || column.header;
            const suggested = suggestLitigationPlanField(header, kind) || suggestLitigationPlanField(column.header, kind) || column.suggestedField;
            return {
                ...column,
                header,
                suggestedField: suggested,
                suggestedScore: suggested ? Math.max(Number(column.suggestedScore) || 0, 82) : column.suggestedScore,
            };
        });
        const matched = columns.filter((column) => column.suggestedField).length;
        return {
            ...sheet,
            sheetKind: kind,
            suggestedHeaderRow: preserveHeaderRow ? (sheet.suggestedHeaderRow || 1) : (matched >= 2 ? 1 : (sheet.suggestedHeaderRow || 1)),
            columns,
        };
    });
    const casesSheet = sheets.find((sheet) => sheetKindOf(sheet) === 'cases');
    return {
        fileId: payload.fileId || fileId,
        fileName: payload.fileName,
        activeSheetName: payload.activeSheetName || casesSheet?.name || sheets[0]?.name,
        sheets,
        standardFields: selectedModuleFields(draft),
    };
}
async function inspectPlanFile(
    fileId: string,
    draft: Draft,
    structureOptions?: {sheetName?: string; headerRow?: number},
): Promise<Inspection> {
    if (isLitigationLedgerPlan(draft)) {
        const payload = await api.post<Inspection>(apiUrl('/api/legal-litigation/plans/inspect'), {
            fileId,
            ...structureOptions,
        });
        return normalizeLitigationInspectPayload(payload, fileId, draft, {
            preserveHeaderRow: structureOptions?.headerRow != null,
            retainFullHeaders: structureOptions?.headerRow != null,
        });
    }
    const inspected = await api.post<{inspection: Inspection}>(apiUrl('/api/ledger/plans/inspect'), {
        fileId,
        ledgerType: draft.ledgerType,
        ...ledgerCapabilityPayload(draft),
        ...structureOptions,
    });
    return inspected.inspection;
}
/**
 * 返回诉讼台账方案列表。
 */
function backToLitigationPlans(): void {
    navigate(LITIGATION_PLANS_PAGE_PATH);
}

/**
 * 绑定「个人 / 团队」互斥勾选（方案性质）。
 * @param form 表单
 */
type LitigationPlanTeamMemberOption = {id: string; displayName: string};

/**
 * 协作单位在下拉框中的名称。
 * @param unit 协作单位
 */
function litigationPlanUnitLabel(unit: LitigationPlanCollaborationUnit): string {
    const titled = unit.shortName ? `${unit.name}（${unit.shortName}）` : unit.name;
    return unit.code ? `${titled} ${unit.code}` : titled;
}

/**
 * 渲染协作单位行。
 * @param units 协作单位
 */
function litigationPlanUnitRowsMarkup(units: LitigationPlanCollaborationUnit[]): string {
    if (!units.length) return '<p class="lp-member-empty">暂无协作单位</p>';
    return units.map((unit, index) => `<div class="lp-unit-row" data-collab-unit="${index}" data-unit-id="${escapeHtml(unit.id)}">
      <input class="lp-control" data-unit-name value="${escapeHtml(unit.name)}" maxlength="200" placeholder="单位名称" aria-label="单位名称">
      <input class="lp-control" data-unit-short value="${escapeHtml(unit.shortName)}" maxlength="40" placeholder="简称" aria-label="简称">
      <input class="lp-control" data-unit-code value="${escapeHtml(unit.code)}" maxlength="40" placeholder="代码" aria-label="代码">
      <button type="button" class="tp-btn-text tp-btn-text-danger" data-remove-unit>移除</button>
    </div>`).join('');
}

/**
 * 渲染协作人员行。人员只能选自当前团队，单位只能选自已添加的协作单位。
 * @param editors 协作人员
 * @param members 当前团队成员
 * @param units 协作单位
 * @param creatorUserId 创建者
 */
function litigationPlanTeamEditorRowsMarkup(
    editors: LitigationPlanTeamEditor[],
    members: LitigationPlanTeamMemberOption[],
    units: LitigationPlanCollaborationUnit[],
    creatorUserId: string,
): string {
    const memberOptions = (userId: string) => members
        .filter((item) => item.id !== creatorUserId)
        .map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === userId ? 'selected' : ''}>${escapeHtml(item.displayName)}</option>`)
        .join('');
    const unitOptions = (unitId: string) => units
        .map((item) => `<option value="${escapeHtml(item.id)}" ${item.id === unitId ? 'selected' : ''}>${escapeHtml(litigationPlanUnitLabel(item))}</option>`)
        .join('');
    if (!editors.length) return '<p class="lp-member-empty">暂无协作人员</p>';
    return editors.map((editor, index) => `<div class="lp-member-row" data-team-editor-row="${index}">
      <select class="lp-control" data-team-editor-user aria-label="协作人员">${memberOptions(editor.userId)}</select>
      <select class="lp-control" data-team-editor-unit aria-label="所属协作单位">${unitOptions(editor.unitId)}</select>
      <button type="button" class="tp-btn-text tp-btn-text-danger" data-remove-team-editor>移除</button>
    </div>`).join('');
}

/**
 * 从编辑页读取协作单位。
 * @param root 表单根节点
 */
function collectLitigationPlanUnits(root: HTMLElement): LitigationPlanCollaborationUnit[] {
    return [...root.querySelectorAll<HTMLElement>('[data-collab-unit]')].map((row, index) => ({
        id: row.dataset.unitId || `unit_${index + 1}`,
        name: String(row.querySelector<HTMLInputElement>('[data-unit-name]')?.value || '').trim(),
        shortName: String(row.querySelector<HTMLInputElement>('[data-unit-short]')?.value || '').trim(),
        code: String(row.querySelector<HTMLInputElement>('[data-unit-code]')?.value || '').trim(),
    }));
}

/**
 * 从编辑页读取协作人员。
 * @param root 表单根节点
 * @param members 当前团队成员
 */
function collectLitigationPlanTeamEditors(
    root: HTMLElement,
    members: LitigationPlanTeamMemberOption[],
): LitigationPlanTeamEditor[] {
    const nameById = new Map(members.map((item) => [item.id, item.displayName]));
    return [...root.querySelectorAll<HTMLElement>('[data-team-editor-row]')].map((row) => {
        const userId = String(row.querySelector<HTMLSelectElement>('[data-team-editor-user]')?.value || '').trim();
        return {
            userId,
            displayName: nameById.get(userId) || '',
            unitId: String(row.querySelector<HTMLSelectElement>('[data-team-editor-unit]')?.value || '').trim(),
        };
    });
}

function bindLitigationPlanScopePickers(form: HTMLFormElement): void {
    const hidden = form.querySelector<HTMLInputElement>('input[name="planScope"]');
    const picks = [...form.querySelectorAll<HTMLInputElement>('[data-plan-scope-pick]')];
    if (!hidden || !picks.length) return;
    const sync = (scope: 'personal' | 'enterprise') => {
        hidden.value = scope;
        picks.forEach((input) => {
            input.checked = input.value === scope;
        });
    };
    picks.forEach((input) => {
        input.addEventListener('change', () => {
            if (input.type === 'checkbox' && !input.checked) {
                input.checked = true;
                return;
            }
            if (input.type === 'radio' && !input.checked) return;
            sync(input.value === 'enterprise' ? 'enterprise' : 'personal');
        });
    });
}

/**
 * 诉讼台账方案编辑：仅方案名称、适用范围与内部案号规则；模板与字段映射由管理后台统一下发。
 */
async function renderLitigationPlanSimpleEditor(
    root: HTMLElement,
    auth: AuthSnapshot,
    editingId: string | null,
    initialName: string,
): Promise<void> {
    renderLayout(root, auth, '<div class="legacy-module-page plans-workflow-page"><div class="plans-loading">正在加载诉讼台账方案…</div></div>');
    let draft: Draft = {
        name: initialName,
        companyName: auth.organization.name,
        ledgerType: 'legal-litigation',
        recognitionModuleId: '',
        capabilityModuleIds: [],
        moduleVersions: {},
        planScope: canUseTeamLedgerPlan(auth) ? 'enterprise' : 'personal',
        internalNoRule: normalizeLitigationInternalNoRule(undefined),
    };
    let isPlanCreator = true;
    let creatorDisplayName = String(auth.user.displayName || auth.user.email || '').trim();
    let creatorOrganizationName = '';
    let creatorOrganizationShortName = '';
    let creatorOrganizationCode = '';
    let organizationLabelKind: LitigationOrganizationLabelKind = 'name';
    let collaborationUnits: LitigationPlanCollaborationUnit[] = [];
    let teamEditors: LitigationPlanTeamEditor[] = [];
    let teamMembers: LitigationPlanTeamMemberOption[] = [];
    let teamMemberLoadError = '';
    try {
        if (canUseTeamLedgerPlan(auth)) {
            try {
                const memberPayload = await api.get<{members?: Array<{id: string; displayName?: string; email?: string}>}>(
                    apiUrl('/api/legal-litigation/team-members'),
                );
                teamMembers = (memberPayload.members ?? []).map((item) => ({
                    id: String(item.id || ''),
                    displayName: String(item.displayName || item.email || item.id || '').trim(),
                })).filter((item) => item.id);
            } catch (error) {
                teamMemberLoadError = error instanceof Error ? error.message : '无法读取团队成员';
            }
        }
        if (editingId) {
            const payload = await api.get<{
                plan: Plan & {
                    internalNoRule?: Draft['internalNoRule'];
                    creatorDisplayName?: string;
                    creatorOrganizationName?: string;
                    creatorOrganizationShortName?: string;
                    creatorOrganizationCode?: string;
                    organizationLabelKind?: string;
                    collaborationUnits?: LitigationPlanCollaborationUnit[];
                    teamEditors?: LitigationPlanTeamEditor[];
                };
                viewer?: {isCreator?: boolean};
            }>(apiUrl(`/api/legal-litigation/plans/${encodeURIComponent(editingId)}`));
            draft = draftFrom(auth, {...payload.plan, ledgerType: 'legal-litigation'}, {litigationOk: true});
            isPlanCreator = payload.viewer?.isCreator !== false;
            if (payload.plan.creatorDisplayName) creatorDisplayName = payload.plan.creatorDisplayName;
            if (payload.plan.creatorOrganizationName) creatorOrganizationName = payload.plan.creatorOrganizationName;
            if (payload.plan.creatorOrganizationShortName) creatorOrganizationShortName = payload.plan.creatorOrganizationShortName;
            if (payload.plan.creatorOrganizationCode) creatorOrganizationCode = payload.plan.creatorOrganizationCode;
            organizationLabelKind = parseLitigationOrganizationLabelKind(String(payload.plan.organizationLabelKind || 'name'));
            const collaboration = normalizeLitigationPlanCollaboration({
                units: payload.plan.collaborationUnits,
                editors: payload.plan.teamEditors,
            });
            collaborationUnits = collaboration.units;
            teamEditors = collaboration.editors;
            draft.creatorDisplayName = creatorDisplayName;
            draft.creatorOrganizationName = creatorOrganizationName;
            draft.creatorOrganizationShortName = creatorOrganizationShortName;
            draft.creatorOrganizationCode = creatorOrganizationCode;
            draft.collaborationUnits = collaborationUnits;
            draft.teamEditors = teamEditors;
        }
        activePlanDraft = draft;
        activePlanAuth = auth;
    } catch (error) {
        renderLayout(root, auth, `<div class="legacy-module-page"><div class="legacy-loading">${escapeHtml(error instanceof Error ? error.message : String(error))}<br><button class="secondary-button" id="backLitigationPlans">返回方案列表</button></div></div>`);
        root.querySelector('#backLitigationPlans')?.addEventListener('click', backToLitigationPlans);
        return;
    }
    const enterpriseScope = draft.planScope === 'enterprise';
    const scopeOptions = canUseTeamLedgerPlan(auth)
        ? `<div class="lp-section">
        <h2 class="lp-section-title">方案所属</h2>
        <div class="lp-segment" role="radiogroup" aria-label="方案所属">
          <label class="lp-segment-item"><input type="radio" name="planScopePick" data-plan-scope-pick value="personal" ${!enterpriseScope ? 'checked' : ''}><span>个人空间</span></label>
          <label class="lp-segment-item"><input type="radio" name="planScopePick" data-plan-scope-pick value="enterprise" ${enterpriseScope ? 'checked' : ''}><span>团队空间</span></label>
        </div>
        <input type="hidden" name="planScope" value="${enterpriseScope ? 'enterprise' : 'personal'}">
      </div>`
        : '<input type="hidden" name="planScope" value="personal">';
    const showCreatorBlock = canUseTeamLedgerPlan(auth) && isPlanCreator;
    const unitList = litigationPlanUnitRowsMarkup(collaborationUnits);
    const teamEditorList = litigationPlanTeamEditorRowsMarkup(teamEditors, teamMembers, collaborationUnits, auth.user.id);
    const internalNoPreview = escapeHtml(previewPlanInternalNo(draft.internalNoRule, draft));
    const scopePanels = showCreatorBlock
        ? `<section class="lp-section lp-team-panel" id="litigationPlanTeamPanel" hidden>
        <h2 class="lp-section-title">台账管理员所属单位显示内容</h2>
        <div class="lp-admin-unit">
          <input class="lp-control" id="lpAdminOrg" maxlength="200" placeholder="所属单位" aria-label="台账管理员所属单位显示内容" value="${escapeHtml(creatorOrganizationName)}">
          <input class="lp-control" id="lpAdminShort" maxlength="40" placeholder="简称" aria-label="方案管理员所属单位简称" value="${escapeHtml(creatorOrganizationShortName)}">
          <input class="lp-control" id="lpAdminCode" maxlength="40" placeholder="代码" aria-label="方案管理员所属单位代码" value="${escapeHtml(creatorOrganizationCode)}">
        </div>
        <h2 class="lp-section-title lp-people-title">台账中的所属单位显示规则</h2>
        <div class="lp-segment lp-segment-3" role="radiogroup" aria-label="台账中的所属单位显示规则">
          ${(Object.keys(LITIGATION_ORGANIZATION_LABEL_KIND_LABELS) as LitigationOrganizationLabelKind[]).map((kind) => `<label class="lp-segment-item"><input type="radio" name="organizationLabelKind" value="${kind}" ${organizationLabelKind === kind ? 'checked' : ''}><span>${escapeHtml(LITIGATION_ORGANIZATION_LABEL_KIND_LABELS[kind])}</span></label>`).join('')}
        </div>
        <p class="lp-org-label-hint">所属单位按方案管理员选择的规则自动填入，团队成员不能改。</p>
        <h2 class="lp-section-title lp-people-title">协作单位</h2>
        <div class="lp-member-list" id="litigationPlanUnitsBody">${unitList}</div>
        <div class="lp-unit-add">
          <input class="lp-control" id="lpUnitName" maxlength="200" placeholder="单位名称" aria-label="单位名称">
          <input class="lp-control" id="lpUnitShort" maxlength="40" placeholder="简称" aria-label="简称">
          <input class="lp-control" id="lpUnitCode" maxlength="40" placeholder="代码" aria-label="代码">
          <button type="button" class="tp-btn-secondary" id="addLitigationPlanUnit">添加</button>
        </div>
        <h2 class="lp-section-title lp-people-title">协作人员</h2>
        <div class="lp-member-list" id="litigationPlanTeamEditorsBody">${teamEditorList}</div>
        <div class="lp-member-add">
          <select class="lp-control" id="lpMemberPick" aria-label="选择团队成员"></select>
          <select class="lp-control" id="lpMemberUnit" aria-label="所属协作单位"></select>
          <button type="button" class="tp-btn-secondary" id="addLitigationPlanTeamEditor">添加</button>
        </div>
        <p class="lp-member-hint" id="lpMemberHint">${escapeHtml(teamMemberLoadError)}</p>
      </section>`
        : '';
    renderLayout(root, auth, `<div class="legacy-module-page litigation-plan-simple-page">
      <form id="litigationSimplePlanForm" class="lp-plan-form">
        <header class="lp-plan-head">
          <div class="lp-plan-head-actions">
            <p class="lp-form-message" id="planMessage" role="status"></p>
            <button class="tp-btn-primary" type="submit">${editingId ? '保存' : '创建'}</button>
          </div>
        </header>
        <div class="lp-plan-body">
          <section class="lp-basics">
            <label class="lp-field lp-field-full">
              <span class="lp-label">方案名称</span>
              <input class="lp-control lp-control-lg" name="name" value="${escapeHtml(draft.name || initialName)}" required maxlength="120" placeholder="例如：法务合规助手" autocomplete="off">
            </label>
            ${scopeOptions}
          </section>
          <section class="lp-rule-row">
            <div class="lp-rule-copy">
              <span class="lp-label">内部案号示意</span>
              <span class="lp-no-preview" data-no-preview>${internalNoPreview}</span>
            </div>
          </section>
          ${scopePanels}
        </div>
      </form>
    </div>`);
    const form = root.querySelector<HTMLFormElement>('#litigationSimplePlanForm');
    if (form) bindLitigationPlanScopePickers(form);
    const teamPanel = root.querySelector<HTMLElement>('#litigationPlanTeamPanel');
    const unitsBody = root.querySelector<HTMLElement>('#litigationPlanUnitsBody');
    const teamEditorsBody = root.querySelector<HTMLElement>('#litigationPlanTeamEditorsBody');
    const memberPick = root.querySelector<HTMLSelectElement>('#lpMemberPick');
    const memberUnit = root.querySelector<HTMLSelectElement>('#lpMemberUnit');
    const memberHint = root.querySelector<HTMLElement>('#lpMemberHint');
    const syncCreatorFields = () => {
        if (!form || !teamPanel) return;
        const scope = String(form.querySelector<HTMLInputElement>('input[name="planScope"]')?.value || 'personal');
        teamPanel.hidden = scope !== 'enterprise';
    };
    const readUnits = (): LitigationPlanCollaborationUnit[] => (unitsBody ? collectLitigationPlanUnits(root) : collaborationUnits);
    const readTeamEditorsFromTable = (): LitigationPlanTeamEditor[] => (
        teamEditorsBody ? collectLitigationPlanTeamEditors(root, teamMembers) : teamEditors
    );
    const paintMemberPick = (editors: LitigationPlanTeamEditor[], units: LitigationPlanCollaborationUnit[]) => {
        if (memberPick) {
            const used = new Set(editors.map((item) => item.userId));
            const options = teamMembers.filter((item) => item.id !== auth.user.id && !used.has(item.id));
            memberPick.innerHTML = options.length
                ? `<option value="">选择本团队成员</option>${options.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.displayName)}</option>`).join('')}`
                : '<option value="">没有可添加的成员</option>';
            memberPick.disabled = options.length === 0;
        }
        if (memberUnit) {
            const ready = units.filter((item) => item.name && item.shortName && item.code);
            memberUnit.innerHTML = ready.length
                ? `<option value="">选择协作单位</option>${ready.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(litigationPlanUnitLabel(item))}</option>`).join('')}`
                : '<option value="">请先添加协作单位</option>';
            memberUnit.disabled = ready.length === 0;
        }
        if (memberHint && !teamMemberLoadError) {
            memberHint.textContent = '';
        }
    };
    const paintUnits = (units: LitigationPlanCollaborationUnit[]) => {
        if (unitsBody) unitsBody.innerHTML = litigationPlanUnitRowsMarkup(units);
    };
    const paintTeamEditorRows = (editors: LitigationPlanTeamEditor[], units = readUnits()) => {
        if (!teamEditorsBody) return;
        teamEditorsBody.innerHTML = litigationPlanTeamEditorRowsMarkup(editors, teamMembers, units, auth.user.id);
        paintMemberPick(editors, units);
    };
    form?.querySelectorAll('[data-plan-scope-pick]').forEach((input) => {
        input.addEventListener('change', syncCreatorFields);
    });
    syncCreatorFields();
    paintMemberPick(readTeamEditorsFromTable(), readUnits());
    form?.addEventListener('input', (event) => {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-collab-unit]')) return;
        paintMemberPick(readTeamEditorsFromTable(), readUnits());
        teamEditorsBody && (teamEditorsBody.innerHTML = litigationPlanTeamEditorRowsMarkup(readTeamEditorsFromTable(), teamMembers, readUnits(), auth.user.id));
    });
    form?.addEventListener('click', (event) => {
        const target = event.target as HTMLElement;
        const addUnit = target.closest('#addLitigationPlanUnit');
        const removeUnit = target.closest<HTMLButtonElement>('[data-remove-unit]');
        const addEditor = target.closest('#addLitigationPlanTeamEditor');
        const removeEditor = target.closest<HTMLButtonElement>('[data-remove-team-editor]');
        if (!addUnit && !removeUnit && !addEditor && !removeEditor) return;
        if (!form.contains(target)) return;
        event.preventDefault();
        if (addUnit) {
            const name = String(root.querySelector<HTMLInputElement>('#lpUnitName')?.value || '').trim();
            const shortName = String(root.querySelector<HTMLInputElement>('#lpUnitShort')?.value || '').trim();
            const code = String(root.querySelector<HTMLInputElement>('#lpUnitCode')?.value || '').trim();
            if (!name || !shortName || !code) {
                if (memberHint) memberHint.textContent = '请填写协作单位名称、简称和代码';
                return;
            }
            const units = readUnits();
            units.push({id: `unit_${crypto.randomUUID()}`, name, shortName, code});
            const nameInput = root.querySelector<HTMLInputElement>('#lpUnitName');
            const shortInput = root.querySelector<HTMLInputElement>('#lpUnitShort');
            const codeInput = root.querySelector<HTMLInputElement>('#lpUnitCode');
            if (nameInput) nameInput.value = '';
            if (shortInput) shortInput.value = '';
            if (codeInput) codeInput.value = '';
            if (memberHint) memberHint.textContent = '';
            paintUnits(units);
            paintTeamEditorRows(readTeamEditorsFromTable(), units);
            return;
        }
        if (removeUnit) {
            const row = removeUnit.closest<HTMLElement>('[data-collab-unit]');
            const unitId = row?.dataset.unitId || '';
            const editors = readTeamEditorsFromTable();
            if (editors.some((item) => item.unitId === unitId)) {
                if (memberHint) memberHint.textContent = '请先移出该单位下的协作人员';
                return;
            }
            const units = readUnits().filter((item) => item.id !== unitId);
            if (memberHint) memberHint.textContent = '';
            paintUnits(units);
            paintTeamEditorRows(editors, units);
            return;
        }
        if (removeEditor) {
            const row = removeEditor.closest<HTMLElement>('[data-team-editor-row]');
            const index = Number(row?.dataset.teamEditorRow);
            const editors = readTeamEditorsFromTable();
            if (!Number.isFinite(index)) return;
            editors.splice(index, 1);
            if (memberHint) memberHint.textContent = '';
            paintTeamEditorRows(editors);
            return;
        }
        const units = readUnits().filter((item) => item.name && item.shortName && item.code);
        const editors = readTeamEditorsFromTable();
        const pickedId = memberPick?.value || '';
        const unitId = memberUnit?.value || '';
        const nextMember = teamMembers.find((item) => item.id === pickedId && item.id !== auth.user.id);
        if (!units.length) {
            if (memberHint) memberHint.textContent = '请先添加协作单位';
            return;
        }
        if (!nextMember) {
            if (memberHint) memberHint.textContent = teamMembers.some((item) => item.id !== auth.user.id) ? '请先选择协作人员' : (teamMemberLoadError || '当前团队没有其他成员');
            return;
        }
        if (!unitId) {
            if (memberHint) memberHint.textContent = '请选择所属协作单位';
            return;
        }
        if (editors.some((item) => item.userId === nextMember.id)) {
            if (memberHint) memberHint.textContent = '该成员已在列表中';
            return;
        }
        if (memberHint) memberHint.textContent = '';
        editors.push({userId: nextMember.id, displayName: nextMember.displayName, unitId});
        paintTeamEditorRows(editors, units);
    });
    root.querySelector('#lpAdminCode')?.addEventListener('input', () => {
        const preview = root.querySelector<HTMLElement>('#litigationSimplePlanForm [data-no-preview]');
        if (preview) preview.textContent = previewPlanInternalNo(draft.internalNoRule, draft);
    });
    root.querySelector<HTMLFormElement>('#litigationSimplePlanForm')?.addEventListener('submit', async (event) => {
        event.preventDefault();
        const form = event.currentTarget as HTMLFormElement;
        const message = root.querySelector<HTMLElement>('#planMessage');
        const data = new FormData(form);
        const name = String(data.get('name') || '').trim();
        if (!name) {
            if (message) message.textContent = '请填写台账方案名称';
            return;
        }
        draft.name = name;
        draft.planScope = String(data.get('planScope') || 'personal') === 'enterprise' ? 'enterprise' : 'personal';
        const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
        if (submit) submit.disabled = true;
        try {
            if (message) message.textContent = '正在保存…';
            const payload: Record<string, unknown> = {
                name: draft.name,
                companyName: draft.companyName,
                planScope: draft.planScope,
                internalNoRule: normalizeLitigationInternalNoRule(draft.internalNoRule),
                usePlatformTemplate: true,
            };
            if (showCreatorBlock) {
                const adminOrgInput = root.querySelector<HTMLInputElement>('#lpAdminOrg');
                const adminShortInput = root.querySelector<HTMLInputElement>('#lpAdminShort');
                const adminCodeInput = root.querySelector<HTMLInputElement>('#lpAdminCode');
                payload.creatorDisplayName = creatorDisplayName;
                payload.creatorOrganizationName = (adminOrgInput ? adminOrgInput.value : creatorOrganizationName).trim();
                payload.creatorOrganizationShortName = (adminShortInput ? adminShortInput.value : creatorOrganizationShortName).trim();
                payload.creatorOrganizationCode = (adminCodeInput ? adminCodeInput.value : creatorOrganizationCode).trim();
                const labelKind = root.querySelector<HTMLInputElement>('input[name="organizationLabelKind"]:checked')?.value;
                payload.organizationLabelKind = parseLitigationOrganizationLabelKind(String(labelKind || organizationLabelKind));
            }
            if (showCreatorBlock && draft.planScope === 'enterprise') {
                if (!String(payload.creatorOrganizationName || '').trim() || !String(payload.creatorOrganizationShortName || '').trim() || !String(payload.creatorOrganizationCode || '').trim()) {
                    if (message) message.textContent = '请填写台账管理员所属单位的名称、简称和代码';
                    if (submit) submit.disabled = false;
                    return;
                }
                const units = readUnits();
                const editors = readTeamEditorsFromTable();
                if (units.some((item) => !item.name.trim() || !item.shortName.trim() || !item.code.trim())) {
                    if (message) message.textContent = '请填写每个协作单位的名称、简称和代码';
                    if (submit) submit.disabled = false;
                    return;
                }
                const unitIds = new Set(units.map((item) => item.id));
                if (editors.some((item) => !unitIds.has(item.unitId))) {
                    if (message) message.textContent = '请为每位协作人员选择所属协作单位';
                    if (submit) submit.disabled = false;
                    return;
                }
                payload.collaborationUnits = units;
                payload.teamEditors = editors;
            }
            if (editingId) {
                await api.patch(apiUrl(`/api/legal-litigation/plans/${encodeURIComponent(editingId)}`), payload);
            } else {
                await api.post(apiUrl('/api/legal-litigation/plans'), payload);
            }
            backToLitigationPlans();
        } catch (error) {
            if (submit) submit.disabled = false;
            if (message) message.textContent = error instanceof Error ? error.message : String(error);
        }
    });
}

/**
 * 诉讼台账方案专项编辑入口（不再开展字段映射与工作表配置）。
 * @param root 根节点
 * @param auth 登录快照
 */
export async function renderLitigationPlanEditorPage(root: HTMLElement, auth: AuthSnapshot): Promise<void> {
    if (!canManageLitigationPlans()) {
        renderLayout(root, auth, '<div class="legacy-module-page"><div class="legacy-loading">诉讼台账方案已关闭。</div></div>');
        return;
    }
    const planId = litigationPlanEditorPlanId() || null;
    const nameFromQuery = (new URLSearchParams(window.location.search).get('name') || '').trim();
    await renderLitigationPlanSimpleEditor(root, auth, planId, nameFromQuery);
}
export async function renderPlanPage(root: HTMLElement, auth: AuthSnapshot) {
    const financeOk = canManageFinancePlans();
    const litigationOk = canManageLitigationPlans();
    if (!financeOk && !litigationOk) {
        renderLayout(root, auth, '<div class="plans-page"><div class="plans-loading">请先选择一个台账类任务，再查看台账方案。</div></div>');
        setTimeout(() => navigate('/tax-assistant/tasks'), 1200);
        return;
    }
        renderLayout(root, auth, '<div class="plans-page"><div class="plans-loading">加载中…</div></div>'); try {
    const emptyCatalog = {defaultRecognitionModuleId: 'customs-declaration-unified-v1', modules: [] as Array<Omit<PlanModule, 0 | 1>>, systemFields: [] as Field[]};
    const [plansPayload, catalog, litigationPayload] = await Promise.all([
        financeOk ? api.get<{plans: Plan[]}>(apiUrl('/api/ledger/plans')) : Promise.resolve({plans: [] as Plan[]}),
        financeOk ? api.get<{
            defaultRecognitionModuleId: string;
            modules: Array<Omit<PlanModule, 0 | 1>>;
            systemFields?: Field[];
        }>(apiUrl('/api/ledger/plan-modules')) : Promise.resolve(emptyCatalog),
        litigationOk ? api.get<{plans: Array<{id: string; name: string; companyName?: string; fileName?: string; sheetName?: string; templateFileId?: string; updatedAt: string}>}>(apiUrl('/api/legal-litigation/plans')) : Promise.resolve({plans: []}),
    ]);
    capabilities = catalog.modules.map(item => Object.assign([item.id, item.name], item) as PlanModule);
    systemFields = catalog.systemFields ?? [];
    defaultRecognitionModuleId = catalog.defaultRecognitionModuleId;
    const merged = [
        ...plansPayload.plans.map((plan) => ({...plan, planKind: 'ledger' as const})),
        ...litigationPayload.plans.map(asLitigationListPlan),
    ].sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)));
    renderList(root, auth, merged, {financeOk, litigationOk});
}
catch (error) {
        renderLayout(root, auth, `<div class="plans-page"><div class="plans-loading">${escapeHtml(error instanceof Error ? error.message : String(error))}</div></div>`);
} }
function renderList(root: HTMLElement, auth: AuthSnapshot, plans: Plan[], access: {financeOk: boolean; litigationOk: boolean}) {
    const matchingCount = plans.filter(plan => !isLitigationListPlan(plan) && plan.ledgerType !== 'invoice-ledger').length;
    const invoiceCount = plans.filter(plan => plan.ledgerType === 'invoice-ledger').length;
    const litigationCount = plans.filter(isLitigationListPlan).length;
    const preferLitigation = requestedPlanKind() === 'legal-litigation' && access.litigationOk;
    const defaultFilter = preferLitigation ? 'legal-litigation' : 'all';
    renderLayout(root, auth, `<div class="plans-page">
    <header class="tp-page-head">
      <div class="tp-page-head-main">
        <p class="tp-eyebrow">方案库</p>
        <h1 class="tp-title">台账方案</h1>
        <p class="tp-hero-desc">在同一页创建、编辑、删除方案。台账类型可选报关单及发票匹配、发票台账${access.litigationOk ? '或诉讼台账' : ''}。</p>
      </div>
      <div class="tp-stat-grid" aria-label="方案概览">
        <div class="tp-stat"><strong>${plans.length}</strong><span>全部方案</span></div>
        ${access.financeOk ? `<div class="tp-stat"><strong>${matchingCount}</strong><span>报关匹配</span></div>
        <div class="tp-stat"><strong>${invoiceCount}</strong><span>发票台账</span></div>` : ''}
        ${access.litigationOk ? `<div class="tp-stat"><strong>${litigationCount}</strong><span>诉讼台账</span></div>` : ''}
      </div>
    </header>

    <div class="tp-toolbar">
      <div class="tp-filter-bar" role="tablist" aria-label="方案筛选">
        <button type="button" class="tp-chip${defaultFilter === 'all' ? ' is-active' : ''}" data-plan-filter="all" role="tab" aria-selected="${defaultFilter === 'all' ? 'true' : 'false'}">全部</button>
        ${access.financeOk ? `<button type="button" class="tp-chip" data-plan-filter="customs-invoice-matching" role="tab" aria-selected="false">报关单及发票匹配</button>
        <button type="button" class="tp-chip" data-plan-filter="invoice-ledger" role="tab" aria-selected="false">发票台账</button>` : ''}
        ${access.litigationOk ? `<button type="button" class="tp-chip${defaultFilter === 'legal-litigation' ? ' is-active' : ''}" data-plan-filter="legal-litigation" role="tab" aria-selected="${defaultFilter === 'legal-litigation' ? 'true' : 'false'}">诉讼台账</button>` : ''}
      </div>
      <div class="tp-toolbar-right">
        <div class="tp-search-group">
          <div class="tp-search">
            <svg class="tp-search-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><circle cx="6" cy="6" r="4.5" stroke="currentColor" stroke-width="1.4"/><path d="m9.5 9.5 3 3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            <input class="tp-search-input" id="planListSearch" placeholder="搜索方案名称、企业或模板文件" aria-label="搜索台账方案">
          </div>
          <button class="tp-btn-search" id="planSearchButton" type="button">搜索</button>
        </div>
        <button class="tp-btn-primary" id="newPlan" type="button">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M7 1v12M1 7h12" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          新建台账方案
        </button>
      </div>
    </div>

    ${plans.length ? plans.map(planCard).join('') : `
    <div class="plans-empty">
      <div class="plans-empty-icon" aria-hidden="true">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none"><rect x="9" y="6" width="22" height="28" rx="3.5" stroke="currentColor" stroke-width="1.6"/><path d="M14 16h12M14 21h12M14 26h8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      </div>
      <h3>还没有台账方案</h3>
      <p>${preferLitigation ? '选定诉讼台账后使用系统标准四表模板，配置字段映射并保存，即可在新建诉讼台账时选用' : '创建第一个方案，配置模板字段映射后即可在识别流程中使用'}</p>
      <button class="tp-btn-primary" id="newPlanEmpty" type="button">新建台账方案</button>
    </div>`}
  </div>`);
    const start = () => {
        setupReturnHref = `${window.location.pathname}${window.location.search || ''}`;
        const fromLitigationList = requestedPlanKind() === 'legal-litigation';
        window.history.replaceState({}, '', '/tax-assistant/plans');
        renderSetup(root, auth, null, {...access, fromLitigationList});
    };
    root.querySelector('#newPlan')?.addEventListener('click', start);
    root.querySelector('#newPlanEmpty')?.addEventListener('click', start);
    let activeFilter = defaultFilter;
    const applyFilters = () => {
        const input = root.querySelector<HTMLInputElement>('#planListSearch');
        const q = (input?.value || '').trim().toLowerCase();
        root.querySelectorAll<HTMLElement>('[data-plan-search]').forEach(row => {
            const matchesSearch = !q || String(row.dataset.planSearch || '').toLowerCase().includes(q);
            const matchesFilter = activeFilter === 'all' || row.dataset.planType === activeFilter;
            row.hidden = !(matchesSearch && matchesFilter);
        });
    };
    root.querySelectorAll<HTMLButtonElement>('[data-plan-filter]').forEach(button => button.addEventListener('click', () => {
        activeFilter = button.dataset.planFilter || 'all';
        const url = new URL(window.location.href);
        if (activeFilter === 'legal-litigation') url.searchParams.set('kind', 'legal-litigation');
        else url.searchParams.delete('kind');
        window.history.replaceState({}, '', `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''}`);
        root.querySelectorAll<HTMLButtonElement>('[data-plan-filter]').forEach(chip => {
            const active = chip === button;
            chip.classList.toggle('is-active', active);
            chip.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        applyFilters();
    }));
    root.querySelector<HTMLInputElement>('#planListSearch')?.addEventListener('input', applyFilters);
    root.querySelector('#planSearchButton')?.addEventListener('click', applyFilters);
    root.querySelector<HTMLInputElement>('#planListSearch')?.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); applyFilters(); }
    });
    root.querySelectorAll<HTMLButtonElement>('[data-edit-plan]').forEach(button => button.addEventListener('click', () => { const plan = plans.find(item => item.id === button.dataset.editPlan); if (plan)
        void editPlan(root, auth, plan); }));
    root.querySelectorAll<HTMLButtonElement>('[data-copy-plan]').forEach(button => button.addEventListener('click', async () => { try {
        await api.post(apiUrl(`/api/ledger/plans/${encodeURIComponent(button.dataset.copyPlan || '')}/copy`));
        await renderPlanPage(root, auth);
    }
    catch (error) {
        alert(error instanceof Error ? error.message : String(error));
    } }));
    root.querySelectorAll<HTMLButtonElement>('[data-plan-history]').forEach(button => button.addEventListener('click', () => void renderPlanHistory(root, auth, button.dataset.planHistory || '')));
    root.querySelectorAll<HTMLButtonElement>('[data-delete-plan]').forEach(button => button.addEventListener('click', async () => {
        const plan = plans.find(item => item.id === button.dataset.deletePlan);
        const litigation = plan ? isLitigationListPlan(plan) : false;
        if (!confirm(litigation ? '确定删除该诉讼台账方案吗？已创建的台账工作簿不受影响。' : '确定删除该台账方案吗？原始模板文件记录会继续保留。'))
            return;
        try {
            if (litigation) await api.delete(apiUrl(`/api/legal-litigation/plans/${encodeURIComponent(button.dataset.deletePlan || '')}`));
            else await api.delete(apiUrl(`/api/ledger/plans/${encodeURIComponent(button.dataset.deletePlan || '')}`));
            await renderPlanPage(root, auth);
        }
        catch (error) {
            alert(error instanceof Error ? error.message : String(error));
        }
    }));
    applyFilters();
}
function planCard(plan: Plan) {
    const litigation = isLitigationListPlan(plan);
    const ledgerType = litigation ? 'legal-litigation' : (plan.ledgerType === 'invoice-ledger' ? 'invoice-ledger' : 'customs-invoice-matching');
    const typeLabel = ledgerType === 'invoice-ledger' ? '发票台账' : ledgerType === 'legal-litigation' ? '诉讼台账' : '报关单及发票匹配台账';
    const typeTheme = ledgerType === 'invoice-ledger' ? 'plans-type-invoice' : ledgerType === 'legal-litigation' ? 'plans-type-litigation' : 'plans-type-matching';
    const search = escapeHtml(`${plan.name} ${plan.companyName} ${typeLabel} ${plan.fileName || ''}`);
    const updatedAt = escapeHtml(new Date(plan.updatedAt).toLocaleString('zh-CN'));
    return `<article class="plans-row" data-plan-search="${search}" data-plan-type="${ledgerType}">
    <div class="plans-row-icon ${typeTheme}" aria-hidden="true">${ledgerType === 'invoice-ledger'
        ? '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4.5" y="3.5" width="11" height="13" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7.5 8h5M7.5 11h5M7.5 14h3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
        : ledgerType === 'legal-litigation'
        ? '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="4" y="3" width="12" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 8h6M7 11h6M7 14h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
        : '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M4.5 6.5h11M4.5 10h7.5M4.5 13.5h9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="14.5" cy="13.5" r="2.5" stroke="currentColor" stroke-width="1.5"/></svg>'}</div>
    <div class="plans-row-main">
      <div class="plans-row-top">
        <h2 class="plans-row-name">${escapeHtml(plan.name)}</h2>
        <span class="plans-meta-chip">${escapeHtml(typeLabel)}</span>
        ${litigation ? '' : `<span class="plans-meta-chip">${plan.planScope === 'enterprise' ? '团队方案' : '个人方案'}</span>`}
      </div>
      <div class="plans-row-meta">
        <span>${escapeHtml(plan.companyName)}</span>
        <span>模板：${escapeHtml(plan.fileName || '未绑定')}</span>
        <span class="plans-row-time">${updatedAt}</span>
      </div>
    </div>
    <div class="plans-row-actions">
      ${litigation ? '' : `<button class="tp-btn-icon" type="button" data-copy-plan="${escapeHtml(plan.id)}" title="复制方案" aria-label="复制方案">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true"><rect x="5" y="5" width="7.5" height="7.5" rx="1.2" stroke="currentColor" stroke-width="1.3"/><path d="M4.5 10V4.2A1.2 1.2 0 0 1 5.7 3h5.8" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
      </button>`}
      <button class="tp-btn-icon tp-btn-icon-danger" type="button" data-delete-plan="${escapeHtml(plan.id)}" title="删除方案" aria-label="删除方案">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true"><path d="M3.5 4.5h8M5.5 4.5V3.5h4v1M6 7v3.5M9 7v3.5M4.5 4.5l.5 7a1 1 0 0 0 1 .9h3a1 1 0 0 0 1-.9l.5-7" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <button class="tp-btn-go" type="button" data-edit-plan="${escapeHtml(plan.id)}">
        编辑方案
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="m4.5 2.5 3.5 3.5-3.5 3.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </div>
  </article>`;
}
async function renderPlanHistory(root:HTMLElement,auth:AuthSnapshot,planId:string):Promise<void>{
    if(!planId)return;
    renderLayout(root,auth,'<div class="legacy-module-page"><div class="legacy-loading">正在读取方案版本和使用任务…</div></div>');
    try{
        const [history,usage]=await Promise.all([
            api.get<{currentVersion:number;versions:Array<{version:number;createdAt:string;snapshot:Plan}>}>(apiUrl(`/api/ledger/plans/${encodeURIComponent(planId)}/versions`)),
            api.get<{tasks:Array<{taskId:string;taskName:string;planVersion:number;lockedAt:string;moduleVersions:Record<string,string>}>}>(apiUrl(`/api/ledger/plans/${encodeURIComponent(planId)}/usage`)),
        ]);
        const current=history.versions[0]?.snapshot;
        renderLayout(root,auth,`<div class="legacy-module-page plans-workflow-page"><div class="template-workflow-toolbar"><strong>${escapeHtml(current?.name||'台账方案')} · 版本历史</strong><button class="secondary-button" id="backPlanHistory">返回方案列表</button></div><section class="admin-panel"><div class="panel-heading"><div><h2>当前版本 v${history.currentVersion}</h2><p class="panel-note">历史版本只读保存，不会随当前方案修改而改变。</p></div></div><div class="table-scroll"><table><thead><tr><th>版本</th><th>模板</th><th>字段映射</th><th>模块版本</th><th>生成时间</th></tr></thead><tbody>${history.versions.map(item=>`<tr><td><b>v${item.version}</b>${item.version===history.currentVersion?' <span class="status status-green">当前</span>':''}</td><td>${escapeHtml(item.snapshot.fileName||'')}</td><td>${item.snapshot.fields?.filter(field=>field.enabled).length||0}</td><td>${escapeHtml(Object.entries(item.snapshot.moduleVersions||{}).map(([id,version])=>`${id}@${version}`).join('、')||'无')}</td><td>${escapeHtml(new Date(item.createdAt).toLocaleString('zh-CN'))}</td></tr>`).join('')}</tbody></table></div></section><section class="admin-panel legacy-card-spaced"><div class="panel-heading"><div><h2>使用该方案的任务</h2><p class="panel-note">任务使用锁定版本，不会自动切换到当前版本。</p></div><span class="legacy-count">${usage.tasks.length} 个任务</span></div><div class="table-scroll"><table><thead><tr><th>任务</th><th>锁定版本</th><th>模块版本</th><th>锁定/更新时间</th></tr></thead><tbody>${usage.tasks.length?usage.tasks.map(task=>`<tr><td>${escapeHtml(task.taskName)}</td><td>v${task.planVersion}</td><td>${escapeHtml(Object.entries(task.moduleVersions).map(([id,version])=>`${id}@${version}`).join('、')||'历史任务未记录')}</td><td>${escapeHtml(new Date(task.lockedAt).toLocaleString('zh-CN'))}</td></tr>`).join(''):'<tr><td colspan="4" class="legacy-empty-cell">当前没有任务锁定该方案</td></tr>'}</tbody></table></div></section></div>`);
        root.querySelector('#backPlanHistory')?.addEventListener('click',()=>void renderPlanPage(root,auth));
    }catch(error){renderLayout(root,auth,`<div class="legacy-module-page"><div class="legacy-loading">${escapeHtml(error instanceof Error?error.message:String(error))}<br><button class="secondary-button" id="backPlanHistory">返回方案列表</button></div></div>`);root.querySelector('#backPlanHistory')?.addEventListener('click',()=>void renderPlanPage(root,auth));}
}
function draftFrom(auth: AuthSnapshot, plan: Plan | null, access: {financeOk?: boolean; litigationOk?: boolean; fromLitigationList?: boolean} = {}): Draft {
    const preferLitigation = plan?.ledgerType === 'legal-litigation' || requestedPlanKind() === 'legal-litigation' || Boolean(access.fromLitigationList) || Boolean(access.litigationOk && !access.financeOk);
    if (preferLitigation && (!plan || plan.ledgerType === 'legal-litigation')) {
        const litigationPlanScope = plan?.planScope === 'personal'
            ? 'personal'
            : (plan?.planScope === 'enterprise' || canUseTeamLedgerPlan(auth) ? 'enterprise' : 'personal');
        const draft = {
            name: plan?.name || '',
            companyName: plan?.companyName || auth.organization.name,
            ledgerType: 'legal-litigation',
            recognitionModuleId: '',
            capabilityModuleIds: [],
            moduleVersions: {},
            headerRow: plan?.headerRow,
            dataStartRow: plan?.dataStartRow,
            listFieldIds: plan && 'listFieldIds' in plan ? plan.listFieldIds : undefined,
            planScope: litigationPlanScope,
            creatorDisplayName: plan?.creatorDisplayName,
            creatorOrganizationName: plan?.creatorOrganizationName,
            creatorOrganizationShortName: plan?.creatorOrganizationShortName,
            creatorOrganizationCode: plan?.creatorOrganizationCode,
            collaborationUnits: normalizeLitigationPlanCollaboration({units: plan?.collaborationUnits, editors: plan?.teamEditors}).units,
            teamEditors: normalizeLitigationPlanCollaboration({units: plan?.collaborationUnits, editors: plan?.teamEditors}).editors,
            internalNoRule: normalizeLitigationInternalNoRule(plan?.internalNoRule),
        };
        activePlanDraft = draft;
        return draft;
    }
    const invoiceLedger = plan?.ledgerType === 'invoice-ledger'; const recognition = plan?.recognitionModuleId || (invoiceLedger ? capabilities.find(item => item.id === 'invoice-unified-v1')?.id : capabilities.find(item => item.category === 'customs-recognition' && item.required)?.id) || defaultRecognitionModuleId; const selected = plan?.capabilityModuleIds?.length ? plan.capabilityModuleIds : (invoiceLedger ? ['invoice-unified-v1','common-field-processing-v1','ledger-export-v1'] : capabilities.filter(item => item.required || item.defaultSelected).map(item => item.id)); const draft = { name: plan?.name || '', companyName: plan?.companyName || auth.organization.name, ledgerType: plan?.ledgerType || 'customs-invoice-matching', recognitionModuleId: recognition, capabilityModuleIds: [...new Set([recognition, ...selected])], moduleVersions: plan?.moduleVersions ?? Object.fromEntries(capabilities.map(item => [item.id, item.version])), headerRow: plan?.headerRow, dataStartRow: plan?.dataStartRow, planScope: plan?.planScope === 'enterprise' && canUseTeamLedgerPlan(auth) ? 'enterprise' : 'personal' }; activePlanDraft = draft; queueMicrotask(() => enhanceModuleOptions(document.querySelector<HTMLFormElement>('#templateSetupForm'), draft)); return draft; }
function enhanceModuleOptions(form: HTMLFormElement | null, draft: Draft): void {
    const target = form?.querySelector<HTMLElement>('.capability-options');
    if (!form || !target) return;
    const invoiceLedger = draft.ledgerType === 'invoice-ledger';
    const visibleCapabilities = invoiceLedger
        ? capabilities.filter(item => item.id !== 'matching-v1' && item.category !== 'customs-recognition' && item.category !== 'refund-library')
        : capabilities;
    target.innerHTML = visibleCapabilities.map(item => item.category === 'customs-recognition'
        ? `<label class="plan-module-option"><input type="radio" name="recognitionModuleId" value="${escapeHtml(item.id)}" ${draft.recognitionModuleId === item.id ? 'checked' : ''}><span><b>${escapeHtml(item.name)}</b>${item.description && item.description !== '系统内置模块' ? `<em>${escapeHtml(item.description)}</em>` : ''}</span></label>`
        : `<label class="plan-module-option"><input type="checkbox" name="capability" value="${escapeHtml(item.id)}" ${draft.capabilityModuleIds.includes(item.id) || item.required ? 'checked' : ''} ${item.required ? 'disabled' : ''}><span><b>${escapeHtml(item.name)}</b>${item.description && item.description !== '系统内置模块' ? `<em>${escapeHtml(item.description)}</em>` : ''}</span></label>${item.required ? `<input type="hidden" name="capability" value="${escapeHtml(item.id)}">` : ''}`
    ).join('') + `<input type="hidden" id="selectedRecognitionCapability" name="capability" value="${escapeHtml(draft.recognitionModuleId)}">`;
    form.querySelector('input[type="hidden"][name="recognitionModuleId"]')?.remove();
    const selected = form.querySelector<HTMLInputElement>('#selectedRecognitionCapability');
    const syncDraft = () => {
        const recognition = form.querySelector<HTMLInputElement>('input[name="recognitionModuleId"]:checked')?.value || draft.recognitionModuleId;
        if (selected) selected.value = recognition;
        draft.recognitionModuleId = recognition;
        draft.capabilityModuleIds = [...new Set([recognition, ...new FormData(form).getAll('capability').map(String)])];
        draft.moduleVersions = Object.fromEntries(draft.capabilityModuleIds.map(id => [id, capabilities.find(item => item.id === id)?.version || draft.moduleVersions?.[id] || '']).filter(([, version]) => Boolean(version)));
        activePlanDraft = draft;
        document.querySelectorAll('.mapping-table .map-source[data-module-filtered]').forEach(item => item.removeAttribute('data-module-filtered'));
        filterMappingFieldOptions();
    };
    form.querySelectorAll<HTMLInputElement>('input[name="recognitionModuleId"],input[name="capability"]').forEach(input => input.addEventListener('change', syncDraft));
    syncDraft();
}
function filterMappingFieldOptions(): void { if (!activePlanDraft)
    return; const sheetName = document.querySelector<HTMLSelectElement>('#sheetSelect')?.value; const all = new Map(planMappingFields(activeInspection, activePlanDraft, sheetName).map(field=>[fieldKey(field),field])); document.querySelectorAll<HTMLSelectElement>('.mapping-table .map-source:not([data-module-filtered])').forEach(select => { const current = select.value; select.innerHTML=groupedFieldOptions([...all.values()],current,true,activePlanDraft); select.value = current; select.dataset.moduleFiltered = '1'; const wrap = select.closest('.map-source-wrap');
    if (wrap) { const parent = wrap.parentElement; if (parent) { parent.insertBefore(select, wrap); wrap.remove(); } initCascadeSelect(select); }
    if (current && current !== '__formula__' && current !== EDIT_INTERNAL_NO_RULE_VALUE && !all.has(current)) {
    select.value = '';
    const checkbox = select.closest('tr')?.querySelector<HTMLInputElement>('.map-enabled');
    if (checkbox)
        checkbox.checked = false;
} }); }
function syncMappingToggle(row: HTMLElement): void {
    const checkbox = row.querySelector<HTMLInputElement>('.map-enabled');
    const toggle = row.querySelector<HTMLButtonElement>('.mapping-toggle');
    if (!checkbox || !toggle) return;
    const enabled = checkbox.checked;
    toggle.classList.toggle('is-enabled', enabled);
    toggle.classList.toggle('is-disabled', !enabled);
    toggle.textContent = enabled ? '启用' : '未启用';
    toggle.setAttribute('aria-pressed', String(enabled));
}
function initCalculationVariableCascade(select: HTMLSelectElement, inspection: Inspection, fields: Field[]): void {
    if(select.dataset.cascadeReady)return;
    select.dataset.cascadeReady='1';
    const wrap=document.createElement('div');
    wrap.className='calculation-variable-wrap';
    select.parentNode?.insertBefore(wrap,select);
    wrap.appendChild(select);
    select.tabIndex=-1;
    select.setAttribute('aria-hidden','true');
    select.classList.add('is-cascade-native-source');
    select.style.display='none';
    const trigger=document.createElement('button');
    trigger.type='button';
    trigger.className='calculation-variable-trigger';
    trigger.innerHTML='<span class="calculation-variable-value">插入可计算字段…</span><span aria-hidden="true">⌄</span>';
    wrap.appendChild(trigger);
    const sheets=inspection.sheets.filter(sheet=>sheet.columns.some(column=>column.header));
    const litigation=isLitigationLedgerPlan();
    const invoiceLedger=isInvoiceLedgerPlan();
    const sourceChildren = (litigation
        ? [{key:'litigation', label:'诉讼台账字段', source:'litigation'}]
        : [
            ...(!invoiceLedger?[{key:'customs', label:'报关单字段', source:'customs'}]:[]),
            {key:'invoice', label:'发票字段', source:'invoice'},
            ...(!invoiceLedger?[{key:'refund', label:'退税率字段', source:'refund'}]:[]),
        ]
    ).map(item=>({
        key:`source::${item.key}`,
        label:item.label,
        items:fields.filter(field=>field.source===item.source).map(field=>({value:fieldKey(field),label:fieldDisplayLabel(field)}))
    })).filter(item=>item.items.length);
    const isCalculableColumn = (column:Column) => {
        if(!column.header)return false;
        const suggested=fields.find(field=>field.id===column.suggestedField && (!column.suggestedModuleId || field.moduleId===column.suggestedModuleId) && (!column.suggestedModuleVersion || field.moduleVersion===column.suggestedModuleVersion));
        if(suggested?.calculationValueType)return true;
        return /金额|数量|重量|单价|价格|税率|税额|汇率|比例|费|率|标的/.test(column.header);
    };
    const groups=[
        ...(litigation?[{key:'litigation',label:'诉讼台账',color:'blue',children:sourceChildren.filter(item=>item.key==='source::litigation')}]:[]),
        ...(!litigation&&!invoiceLedger?[{key:'customs',label:'报关单',color:'blue',children:sourceChildren.filter(item=>item.key==='source::customs')}]:[]),
        ...(!litigation?[{key:'invoice',label:'发票',color:'teal',children:sourceChildren.filter(item=>item.key==='source::invoice')}]:[]),
        ...(!litigation&&!invoiceLedger?[{key:'refund',label:'退税率库',color:'amber',children:sourceChildren.filter(item=>item.key==='source::refund')}]:[]),
        {key:'workbook',label:'工作表',color:'brown',children:sheets.map(sheet=>({key:`sheet::${sheet.name}`,label:sheet.name,items:sheet.columns.filter(isCalculableColumn).map(column=>({value:`${sheet.name}::${column.columnLetter}`,label:column.header}))})).filter(item=>item.items.length)}
    ].filter(group=>group.children.length);
    const panel=document.createElement('div');
    panel.className='calculation-variable-panel';
    panel.hidden=true;
    wrap.appendChild(panel);
    let activeGroup=groups[0]?.key||'';
    let activeChild=groups[0]?.children[0]?.key||'';
    const close=()=>{
        panel.hidden=true;
        trigger.classList.remove('is-open');
        panel.classList.remove('is-submenu-left');
        delete wrap.dataset.insertStart;
        delete wrap.dataset.insertEnd;
        delete wrap.dataset.insertMode;
        delete wrap.dataset.requestedInsertMode;
    };
    const choose=(value:string,display:string) => {
        const expression=wrap.closest('.mapping-advanced-tools')?.querySelector<HTMLTextAreaElement>('.map-calculation-expression');
        const sumMode=wrap.dataset.insertMode==='sum';
        const insertion=sumMode?`SUM([${display}])`:`[${display}]`;
        const insertStart=Number(wrap.dataset.insertStart);
        const insertEnd=Number(wrap.dataset.insertEnd);
        if(expression&&Number.isFinite(insertStart)){
            const start=Math.max(0,Math.min(insertStart,expression.value.length));
            const end=Math.max(start,Math.min(Number.isFinite(insertEnd)?insertEnd:start,expression.value.length));
            expression.value=`${expression.value.slice(0,start)}${insertion}${expression.value.slice(end)}`;
            const caret=start+insertion.length;
            expression.focus();
            expression.setSelectionRange(caret,caret);
        }else if(expression){
            expression.value+=`${expression.value.trim()?' ':''}${insertion}`;
        }
        expression?.dispatchEvent(new Event('input',{bubbles:true}));
        select.value=value;
        trigger.querySelector('.calculation-variable-value')!.textContent=sumMode?`合计：${display}`:display;
        close();
    };
    const render=()=>{
        const group=groups.find(item=>item.key===activeGroup)||groups[0];
        if(!group)return;
        const child=group.children.find(item=>item.key===activeChild)||group.children[0];
        if(!child)return;
        activeChild=child.key;
        const secondItems=group.children.map(item=>`<button type="button" class="calculation-variable-item calculation-variable-child ${item.key===activeChild?'is-active':''}" data-child="${escapeHtml(item.key)}">${escapeHtml(item.label)}<span class="calculation-variable-child-meta"><span class="calculation-variable-child-count">${item.items.length}</span><span class="calculation-variable-chevron" aria-hidden="true">›</span></span></button>`).join('');
        const fieldsHtml=child.items.map(item=>`<button type="button" class="calculation-variable-item calculation-variable-field" data-value="${escapeHtml(item.value)}" data-label="${escapeHtml(item.label)}">${escapeHtml(item.label)}</button>`).join('')||'<span class="calculation-variable-empty">暂无可计算字段</span>';
        panel.innerHTML=`<div class="calculation-variable-categories">${groups.map(item=>`<button type="button" class="calculation-variable-category ${item.color} ${item.key===activeGroup?'is-active':''}" data-group="${item.key}"><span class="calculation-variable-category-label">${escapeHtml(item.label)}</span><span class="calculation-variable-category-meta"><span class="calculation-variable-category-count">${item.children.reduce((sum,entry)=>sum+entry.items.length,0)}</span><span class="calculation-variable-category-chevron" aria-hidden="true">›</span></span></button>`).join('')}</div><div class="calculation-variable-items">${secondItems}</div><div class="calculation-variable-fields">${fieldsHtml}</div>`;
        panel.querySelectorAll<HTMLButtonElement>('[data-group]').forEach(button=>{
            const activate=()=>{const key=button.dataset.group||'';if(activeGroup===key)return;activeGroup=key;activeChild=groups.find(item=>item.key===key)?.children[0]?.key||'';render();};
            button.addEventListener('mouseenter',activate);
            button.addEventListener('focus',activate);
            button.addEventListener('click',activate);
        });
        panel.querySelectorAll<HTMLButtonElement>('[data-child]').forEach(button=>{
            const activate=()=>{const key=button.dataset.child||'';if(activeChild===key)return;activeChild=key;render();};
            button.addEventListener('mouseenter',activate);
            button.addEventListener('focus',activate);
            button.addEventListener('click',activate);
        });
        panel.querySelectorAll<HTMLButtonElement>('.calculation-variable-field').forEach(button=>button.addEventListener('click',()=>choose(button.dataset.value||'',button.dataset.label||'')));
    };
    trigger.addEventListener('click',event=>{
        event.stopPropagation();
        if(!panel.hidden){close();return;}
        const expression=wrap.closest('.mapping-advanced-tools')?.querySelector<HTMLTextAreaElement>('.map-calculation-expression');
        if(expression&&wrap.dataset.insertStart===undefined){
            wrap.dataset.insertStart=String(expression.selectionStart??expression.value.length);
            wrap.dataset.insertEnd=String(expression.selectionEnd??expression.selectionStart??expression.value.length);
        }
        wrap.dataset.insertMode=wrap.dataset.requestedInsertMode==='sum'?'sum':'field';
        delete wrap.dataset.requestedInsertMode;
        render();
        panel.hidden=false;
        trigger.classList.add('is-open');
        panel.classList.remove('is-submenu-left');
    });
    document.addEventListener('click',event=>{if(!wrap.contains(event.target as Node)&&!panel.contains(event.target as Node))close();});
    window.addEventListener('resize',close);
}
function enhanceMappingAdvancedControls(root: HTMLElement): void {
    const inspection = activeInspection;
    if (!inspection) return;
    const fields = planMappingFields(inspection, activePlanDraft, document.querySelector<HTMLSelectElement>('#sheetSelect')?.value);
    const calculationFields = fields.filter(isCalculableField);
    const calculationGroups=new Map<string,Field[]>();calculationFields.forEach(field=>{const label=fieldProviderLabel(field);const items=calculationGroups.get(label)||[];items.push(field);calculationGroups.set(label,items)});
    const workbookVariableOptions = inspection.sheets.map(sheet => {
        const options = sheet.columns.filter(column => column.header).map(column => `<option value="${escapeHtml(`${sheet.name}::${column.columnLetter}`)}">　${escapeHtml(column.header)}</option>`).join('');
        return options ? `<optgroup label="工作表：${escapeHtml(sheet.name)}">${options}</optgroup>` : '';
    }).join('');
    const variableOptions = [...calculationGroups.entries()].map(([label,items])=>`<optgroup label="${escapeHtml(label)}">${items.map(field=>`<option value="${escapeHtml(fieldKey(field))}">${escapeHtml(fieldDisplayLabel(field))}${field.calculationValueType==='percent'?'（按小数参与计算）':''}</option>`).join('')}</optgroup>`).join('') + workbookVariableOptions;
    root.querySelectorAll<HTMLTableRowElement>('[data-map-row]:not([data-advanced-ready])').forEach(row => {
        row.dataset.advancedReady = '1';
        const saved = activeMappingFields.find(field => Number(field.columnIndex) === Number(row.dataset.columnIndex));
        const select = row.querySelector<HTMLSelectElement>('.map-source');
        const cell = select?.closest('td');
        if (!select || !cell) return;
        const litigationPlan = isLitigationLedgerPlan();
        if (!litigationPlan && !select.querySelector('option[value="__formula__"]')) select.add(new Option('自定义计算规则', '__formula__'));
        if (!litigationPlan && !select.querySelector('option[value="__reference__"]')) select.add(new Option('引用其他工作表字段', '__reference__'));
        if (saved?.valueMode === 'formula') select.value = '__formula__';
        if (!litigationPlan && saved?.valueMode === 'reference') select.value = '__reference__';
        const transform = saved?.valueTransform === 'left' || saved?.valueTransform === 'right' || saved?.valueTransform === 'prefix' || saved?.valueTransform === 'suffix' ? saved.valueTransform : '';
        const length = Math.max(1, Math.min(100, Number(saved?.transformLength) || 3));
        const transformText = saved?.transformText || '';
        const decimals = Math.max(0, Math.min(8, Number(saved?.calculationDecimalPlaces ?? 2)));
        const tools = document.createElement('div');
        tools.className = 'mapping-advanced-tools';
        tools.innerHTML = `<div class="mapping-source-meta"></div><div class="mapping-source-tools"><span class="source-tools-leading-sep" aria-hidden="true">丨</span><div class="mapping-field-transform"><select class="form-select map-transform" title="字段处理" aria-label="字段处理"><option value=""${transform === '' ? ' selected' : ''}>保留字段原值</option><option value="left"${transform === 'left' ? ' selected' : ''}>截取开头</option><option value="right"${transform === 'right' ? ' selected' : ''}>截取末尾</option><option value="prefix"${transform === 'prefix' ? ' selected' : ''}>字段前增加</option><option value="suffix"${transform === 'suffix' ? ' selected' : ''}>字段后增加</option></select><span class="map-transform-length-wrap"><input class="form-input map-transform-length" type="number" min="1" max="100" value="${length}"> 位</span><input class="form-input map-transform-text" value="${escapeHtml(transformText)}" maxlength="120" placeholder="请输入要增加的内容" hidden></div><span class="formula-sep" aria-hidden="true">丨</span><button type="button" class="formula-toggle" title="自定义计算规则" aria-label="自定义计算规则">fx</button></div><div class="mapping-calculation-tools"><select class="form-select map-calculation-variable" aria-label="插入可计算字段"><option value="">插入可计算字段…</option>${variableOptions}</select><div class="mapping-formula-row"><textarea class="form-input map-calculation-expression" rows="3" placeholder="选择字段后，将以 [字段名称] 插入公式">${escapeHtml(formulaForDisplay(saved?.calculationExpression || '', calculationFields, inspection))}</textarea></div><div class="map-calculation-result" aria-live="polite"></div><div class="mapping-calculation-quick-tools"><button type="button" class="mapping-sum-field" title="合计字段全部数值" aria-label="合计字段全部数值"><span class="fi" aria-hidden="true">&#xE8EF;</span></button></div><div class="mapping-calculation-settings"><label>结果保留 <input class="form-input map-calculation-decimals" type="number" min="0" max="8" value="${decimals}"> 位小数</label><label>字段为空时 <select class="form-select map-calculation-missing"><option value="error"${saved?.calculationMissingValuePolicy !== 'zero' ? ' selected' : ''}>停止并提示</option><option value="zero"${saved?.calculationMissingValuePolicy === 'zero' ? ' selected' : ''}>按 0 计算</option></select></label><span class="map-test-formula-separator" aria-hidden="true">丨</span><button class="secondary-button map-test-formula" type="button">验证公式</button></div></div>`;
        const sourceLayout = document.createElement('div');
        sourceLayout.className = 'mapping-source-layout';
        const sourceControl = select.closest<HTMLElement>('.map-source-wrap') || select;
        sourceControl.insertAdjacentElement('beforebegin', sourceLayout);
        sourceLayout.append(sourceControl, tools);
        const variableSelect = tools.querySelector<HTMLSelectElement>('.map-calculation-variable');
        if (variableSelect) initCalculationVariableCascade(variableSelect, inspection, calculationFields);
        tools.querySelector<HTMLButtonElement>('.mapping-sum-field')?.addEventListener('click',event=>{
            event.stopPropagation();
            const variableWrap=tools.querySelector<HTMLElement>('.calculation-variable-wrap');
            const variableTrigger=variableWrap?.querySelector<HTMLButtonElement>('.calculation-variable-trigger');
            if(!variableWrap||!variableTrigger)return;
            const expression=tools.querySelector<HTMLTextAreaElement>('.map-calculation-expression');
            if(expression){
                variableWrap.dataset.insertStart=String(expression.selectionStart??expression.value.length);
                variableWrap.dataset.insertEnd=String(expression.selectionEnd??expression.selectionStart??expression.value.length);
            }
            if(variableTrigger.classList.contains('is-open')){
                variableWrap.dataset.insertMode='sum';
                return;
            }
            variableWrap.dataset.requestedInsertMode='sum';
            variableTrigger.click();
        });
        const referenceTools = document.createElement('div');
        referenceTools.className = 'mapping-reference-tools';
        referenceTools.hidden = litigationPlan;
        if (!litigationPlan) {
            referenceTools.innerHTML = `<label><select class="form-select map-reference-sheet"><option value="">选择需引用的工作表</option>${inspection.sheets.map(item => `<option value="${escapeHtml(item.name)}"${item.name === saved?.referenceSheetName ? ' selected' : ''}>${escapeHtml(item.name)}</option>`).join('')}</select></label><label><select class="form-select map-reference-column"><option value="">选择需引用的字段</option></select></label>`;
            sourceLayout.append(referenceTools);
        }
        const referenceSheet = referenceTools.querySelector<HTMLSelectElement>('.map-reference-sheet');
        const referenceColumn = referenceTools.querySelector<HTMLSelectElement>('.map-reference-column');
        const syncReferenceColumns = () => {
            const selectedSheet = inspection.sheets.find(item => item.name === referenceSheet?.value);
            if (!referenceColumn) return;
            referenceColumn.innerHTML = `<option value="">选择需引用的字段</option>${(selectedSheet?.columns ?? []).map(column => `<option value="${column.columnIndex}"${Number(column.columnIndex) === Number(saved?.referenceColumnIndex) ? ' selected' : ''}>${escapeHtml(column.header || '未命名列')}</option>`).join('')}`;
        };
        referenceSheet?.addEventListener('change', syncReferenceColumns);
        if (!litigationPlan) syncReferenceColumns();
        const sourceTools = tools.querySelector<HTMLElement>('.mapping-source-tools');
        const fieldTransform = tools.querySelector<HTMLElement>('.mapping-field-transform');
        const sourceToolsLeadingSeparator = tools.querySelector<HTMLElement>('.source-tools-leading-sep');
        const formulaSeparator = tools.querySelector<HTMLElement>('.formula-sep');
        const formulaToggle = tools.querySelector<HTMLButtonElement>('.formula-toggle');
        const calculationTools = tools.querySelector<HTMLElement>('.mapping-calculation-tools');
        const transformSelect = tools.querySelector<HTMLSelectElement>('.map-transform');
        const lengthWrap = tools.querySelector<HTMLElement>('.map-transform-length-wrap');
        const transformTextInput = tools.querySelector<HTMLInputElement>('.map-transform-text');
        const syncMode = () => {
            const formula = select.value === '__formula__';
            const reference = select.value === '__reference__';
            const selectedField=fields.find(field=>fieldKey(field)===select.value);
            const systemGenerated=selectedField?.source==='computed';
            if (fieldTransform) fieldTransform.hidden = formula||reference;
            if (sourceToolsLeadingSeparator) sourceToolsLeadingSeparator.hidden = formula||reference;
            if (formulaSeparator) formulaSeparator.hidden = formula||reference;
            if (formulaToggle) formulaToggle.classList.toggle('is-active', formula);
            if (calculationTools) calculationTools.hidden = !formula;
            referenceTools.hidden = !reference;
            const meta=tools.querySelector<HTMLElement>('.mapping-source-meta');
            if(meta){
                const litigation=isLitigationLedgerPlan();
                if(litigation){
                    meta.classList.remove('ll-fill-guide');
                    meta.innerHTML = '';
                }else{
                    meta.classList.remove('ll-fill-guide');
                    meta.innerHTML=selectedField?`<span class="mapping-source-pill ${selectedField.source==='computed'?'system':selectedField.source==='custom'?'custom':''}">${escapeHtml(fieldSourceLabel(selectedField))}</span><span>${escapeHtml(fieldDisplayLabel(selectedField))}${selectedField.source==='computed'?' · 由通用字段处理模块自动完成':selectedField.source==='custom'?' · 按本方案自定义规则自动生成':''}</span>`:formula?'<span class="mapping-source-pill formula">计算</span><span>使用多个数值字段生成结果</span>':'<span>该目标列暂不自动抓取</span>';
                }
            }
            const checkbox = row.querySelector<HTMLInputElement>('.map-enabled');
            if (checkbox && (formula || reference)) checkbox.checked = true;
        };
        select.addEventListener('change', syncMode);
        const syncTransformControls = () => { const textMode = transformSelect?.value === 'prefix' || transformSelect?.value === 'suffix'; if (lengthWrap) lengthWrap.hidden = !transformSelect?.value || textMode; if (transformTextInput) transformTextInput.hidden = !textMode; };
        transformSelect?.addEventListener('change', syncTransformControls);
        syncTransformControls();
        const insertCalculationToken=(expression:HTMLTextAreaElement,insertion:string) => {
            const start=Math.max(0,Math.min(expression.selectionStart??expression.value.length,expression.value.length));
            const end=Math.max(start,Math.min(expression.selectionEnd??start,expression.value.length));
            expression.value=`${expression.value.slice(0,start)}${insertion}${expression.value.slice(end)}`;
            const caret=start+insertion.length;
            expression.focus();
            expression.setSelectionRange(caret,caret);
            expression.dispatchEvent(new Event('input',{bubbles:true}));
        };
        tools.querySelector<HTMLSelectElement>('.map-calculation-variable')?.addEventListener('change', event => {
            const variable = event.currentTarget as HTMLSelectElement;
            const expression = tools.querySelector<HTMLTextAreaElement>('.map-calculation-expression');
            const selectedField=calculationFields.find(field=>fieldKey(field)===variable.value);
            if (expression && variable.value.includes('::')) {
                const [sheetName, columnLetter] = variable.value.split('::');
                const column = inspection.sheets.find(sheet => sheet.name === sheetName)?.columns.find(item => item.columnLetter === columnLetter);
                insertCalculationToken(expression,`[${column?.header || columnLetter}]`);
            } else if (selectedField && expression) insertCalculationToken(expression,`[${fieldDisplayLabel(selectedField)}]`);
            variable.value = '';
        });
        tools.querySelector<HTMLButtonElement>('.map-test-formula')?.addEventListener('click', async event => {
            const button = event.currentTarget as HTMLButtonElement;
            const expression = tools.querySelector<HTMLTextAreaElement>('.map-calculation-expression');
            const result = tools.querySelector<HTMLElement>('.map-calculation-result');
            if (!expression?.value.trim() || !result) { if (result) result.textContent = '公式为空'; return; }
            button.disabled = true;
            result.textContent = '正在检查公式…';
            try {
            const storageExpression=formulaForStorage(expression.value, calculationFields, inspection);
                const payload = await api.post<{normalizedExpression:string;fields:string[]}>(apiUrl('/api/ledger/calculations/validate'), {expression: storageExpression, ...ledgerCapabilityPayload()});
                expression.value = formulaForDisplay(payload.normalizedExpression, calculationFields, inspection);
                const friendlyFields = payload.fields.map(reference => {
                    const matched = calculationFields.find(field => fieldKey(field) === reference);
                    const module = matched ? capabilities.find(item => item.id === matched.moduleId) : undefined;
                    return matched ? `${matched.label || matched.id} · ${module?.name || matched.moduleId}` : reference;
                });
                result.textContent = `✓ 公式正确${friendlyFields.length ? `；引用：${friendlyFields.join('、')}` : ''}`;
                result.className = 'map-calculation-result success';
            } catch (error) {
                result.textContent = error instanceof Error ? error.message : String(error);
                result.className = 'map-calculation-result error';
            } finally { button.disabled = false; }
        });
        syncMode();
    });
    if (!root.dataset.formulaToggleBound) {
        root.dataset.formulaToggleBound = '1';
        root.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const btn = target.closest('.mapping-source-tools .formula-toggle') as HTMLButtonElement | null;
            if (!btn) return;
            e.stopPropagation();
            const row = btn.closest('[data-map-row]') as HTMLElement | null;
            const select = row?.querySelector<HTMLSelectElement>('.map-source');
            if (!select) return;
            const isFormula = select.value === '__formula__';
            if (isFormula) {
                const lastVal = btn.dataset.lastSourceValue || '';
                select.value = lastVal;
                select.closest('.map-source-wrap')?.classList.remove('is-formula-mode');
            } else {
                btn.dataset.lastSourceValue = select.value;
                select.value = '__formula__';
                select.closest('.map-source-wrap')?.classList.add('is-formula-mode');
            }
            select.dispatchEvent(new Event('change', { bubbles: true }));
        });
        root.addEventListener('change', (e) => {
            const target = e.target as HTMLElement;
            if (!target.classList?.contains('map-source')) return;
            const row = target.closest('[data-map-row]') as HTMLElement | null;
            const btn = row?.querySelector<HTMLButtonElement>('.mapping-source-tools .formula-toggle');
            if (!btn) return;
            const select = target as HTMLSelectElement;
            const isFormula = select.value === '__formula__';
            btn.classList.toggle('is-active', isFormula);
            select.closest('.map-source-wrap')?.classList.toggle('is-formula-mode', isFormula);
            if (!isFormula) btn.dataset.lastSourceValue = select.value;
        });
    }
}
function syncLedgerTypeControls(): void {
    const form = document.querySelector<HTMLFormElement>('#templateSetupForm');
    const select = form?.querySelector<HTMLSelectElement>('select[name="ledgerType"]');
    if (!form || !select || !activePlanDraft || select.dataset.ledgerTypeBound)
        return;
    select.dataset.ledgerTypeBound = '1';
    select.addEventListener('change', () => {
        if (!activePlanDraft)
            return;
        activePlanDraft.ledgerType = select.value === 'invoice-ledger' ? 'invoice-ledger' : select.value === 'legal-litigation' ? 'legal-litigation' : 'customs-invoice-matching';
        if (activePlanDraft.ledgerType === 'legal-litigation') {
            syncSetupWizardChrome(form.closest('.plans-workflow-page') || document);
            return;
        }
        const invoiceLedger = activePlanDraft.ledgerType === 'invoice-ledger';
        const recognition = invoiceLedger
            ? capabilities.find(item => item.id === 'invoice-unified-v1')?.id || 'invoice-unified-v1'
            : capabilities.find(item => item.category === 'customs-recognition' && item.required)?.id || defaultRecognitionModuleId;
        activePlanDraft.recognitionModuleId = recognition;
        activePlanDraft.capabilityModuleIds = invoiceLedger
            ? ['invoice-unified-v1', 'common-field-processing-v1', 'ledger-export-v1']
            : [...new Set([recognition, ...capabilities.filter(item => item.required || item.defaultSelected).map(item => item.id)])];
        activePlanDraft.moduleVersions = Object.fromEntries(activePlanDraft.capabilityModuleIds.map(id => [id, capabilities.find(item => item.id === id)?.version || '']).filter(([, version]) => Boolean(version)));
        enhanceModuleOptions(form, activePlanDraft);
        const summary = form.querySelector<HTMLElement>('#capabilitySummary');
        if (summary)
            summary.textContent = `已选择 ${activePlanDraft.capabilityModuleIds.length} 个模块`;
        syncSetupWizardChrome(form.closest('.plans-workflow-page') || document);
    });
}
function syncSetupDraft(form: HTMLFormElement, auth: AuthSnapshot): void {
    if (!activePlanDraft)
        return;
    const data = new FormData(form);
    activePlanDraft.name = String(data.get('name') || '').trim();
    activePlanDraft.companyName = auth.organization.name;
    activePlanDraft.ledgerType = String(data.get('ledgerType') || 'customs-invoice-matching');
    const invoiceLedger = activePlanDraft.ledgerType === 'invoice-ledger';
    const selectedModules = [...new Set(data.getAll('capability').map(String))];
    const selectedRecognition = selectedModules.find(id => capabilities.find(item => item.id === id)?.category === (invoiceLedger ? 'invoice-recognition' : 'customs-recognition'));
    activePlanDraft.recognitionModuleId = selectedRecognition
        || String(data.get('recognitionModuleId') || activePlanDraft.recognitionModuleId || defaultRecognitionModuleId);
    activePlanDraft.capabilityModuleIds = selectedModules.length
        ? selectedModules
        : [activePlanDraft.recognitionModuleId];
    activePlanDraft.moduleVersions = Object.fromEntries(activePlanDraft.capabilityModuleIds.map(id => [id, capabilities.find(item => item.id === id)?.version || activePlanDraft?.moduleVersions?.[id] || '']).filter(([, version]) => Boolean(version)));
}
/**
 * 新建向导在选择诉讼台账时仍停在基本信息页，不把本页当成专项编辑页。
 * @param root 向导根节点
 */
function syncSetupWizardChrome(root: ParentNode = document): void {
    const form = root.querySelector<HTMLFormElement>('#templateSetupForm');
    if (!form) return;
    const type = String((form.elements.namedItem('ledgerType') as HTMLSelectElement)?.value || activePlanDraft?.ledgerType || '');
    const litigation = type === 'legal-litigation';
    const uploadVisible = Boolean(root.querySelector<HTMLElement>('#templateUploadSurface') && !root.querySelector<HTMLElement>('#templateUploadSurface')?.hidden);
    const desc = root.querySelector<HTMLElement>('#planWorkflowDesc');
    const stepUpload = root.querySelector<HTMLElement>('.plans-workflow-step[data-step="upload"]');
    if (desc && !uploadVisible) {
        desc.textContent = litigation
            ? '填写方案名称与台账类型，下一步保存诉讼方案（模板与字段由管理后台统一下发）'
            : '填写方案名称与台账类型，完成后进入模板上传';
    }
    if (stepUpload) stepUpload.hidden = litigation;
}
function bindPlanSetupStep(root: HTMLElement): void {
    const form = root.querySelector<HTMLFormElement>('#templateSetupForm');
    const upload = root.querySelector<HTMLElement>('#templateUploadSurface');
    const setup = form?.closest<HTMLElement>('.template-plan-setup');
    if (!form || !upload || !setup || form.dataset.stepBound)
        return;
    form.dataset.stepBound = '1';
    upload.hidden = true;
    const next = document.createElement('button');
    next.type = 'button';
    next.className = 'tp-btn-primary plans-workflow-next';
    next.textContent = '下一步';
    form.after(next);
    const message = root.querySelector<HTMLElement>('#planMessage');
    const toolbarTitle = root.querySelector<HTMLElement>('#planWorkflowTitle');
    const workflowDesc = root.querySelector<HTMLElement>('#planWorkflowDesc');
    const workflowBack = root.querySelector<HTMLButtonElement>('#planWorkflowBack');
    const stepSetup = root.querySelector<HTMLElement>('.plans-workflow-step[data-step="setup"]');
    const stepUpload = root.querySelector<HTMLElement>('.plans-workflow-step[data-step="upload"]');
    const refreshUploadState = (): void => {
        const isReady = Boolean(String(form.elements.namedItem('name')?.value || '').trim());
        root.querySelector('#templateDropZone')?.classList.toggle('locked', !isReady);
        const status = root.querySelector<HTMLElement>('#uploadStepStatus');
        if (status) {
            status.textContent = isReady ? '可以上传' : '等待填写';
            status.className = `plans-status-pill ${isReady ? 'is-ready' : 'is-warn'}`;
        }
        const note = root.querySelector<HTMLElement>('#templateUploadLockedNote');
        if (note)
            note.hidden = isReady;
    };
    const workflowTitle = () => form.dataset.editMode === '1' ? '编辑台账方案' : '新建台账方案';
    const showUpload = () => {
        if (!activePlanAuth) {
            const message = root.querySelector<HTMLElement>('#planMessage');
            if (message)
                message.textContent = '登录状态已失效，请刷新后重新登录';
            return;
        }
        syncSetupDraft(form, activePlanAuth);
        setup.hidden = true;
        next.hidden = true;
        upload.hidden = false;
        if (toolbarTitle)
            toolbarTitle.textContent = '识别企业台账格式';
        if (workflowDesc)
            workflowDesc.textContent = '上传企业现有台账 Excel 模板，系统将自动识别表头与字段结构';
        if (workflowBack)
            workflowBack.hidden = false;
        stepSetup?.classList.remove('is-active');
        stepUpload?.classList.add('is-active');
        refreshUploadState();
    };
    const showSetup = () => {
        setup.hidden = false;
        next.hidden = false;
        upload.hidden = true;
        if (toolbarTitle)
            toolbarTitle.textContent = workflowTitle();
        if (workflowDesc)
            workflowDesc.textContent = '填写方案名称与台账类型，完成后进入模板上传';
        if (workflowBack)
            workflowBack.hidden = true;
        stepUpload?.classList.remove('is-active');
        stepSetup?.classList.add('is-active');
    };
    next.addEventListener('click', async () => {
        const name = String(form.elements.namedItem('name')?.value || '').trim();
        if (!name) {
            if (message)
                message.textContent = '请先填写台账方案名称';
            return;
        }
        const ledgerType = String((form.elements.namedItem('ledgerType') as HTMLSelectElement)?.value || '');
        const editingId = form.dataset.editingPlanId || '';
        try {
            const listUrl = ledgerType === 'legal-litigation' ? '/api/legal-litigation/plans' : '/api/ledger/plans';
            const payload = await api.get<{ plans: Array<{ id: string; name: string }> }>(apiUrl(listUrl));
            const duplicate = payload.plans.some(plan => plan.id !== editingId && plan.name.trim() === name);
            if (duplicate) {
                if (message) message.textContent = '该台账方案名称已存在，请更换方案名称后再继续';
                return;
            }
        } catch (error) {
            if (message) message.textContent = error instanceof Error ? `无法校验方案名称：${error.message}` : '无法校验方案名称，请稍后重试';
            return;
        }
        if (message)
            message.textContent = '';
        if (ledgerType === 'legal-litigation') {
            navigate(litigationPlanEditorHref({planId: editingId || undefined, name}));
            return;
        }
        showUpload();
    });
    workflowBack?.addEventListener('click', showSetup);
    form.querySelector('select[name="ledgerType"]')?.addEventListener('change', () => syncSetupWizardChrome(root));
    syncSetupWizardChrome(root);
}
const planPageRoot = document.querySelector<HTMLElement>('#root');
if (planPageRoot)
    new MutationObserver(() => { bindPlanSetupStep(planPageRoot); syncLedgerTypeControls(); ensureEditorModuleOptions(); filterMappingFieldOptions(); enhanceMappingAdvancedControls(planPageRoot); }).observe(planPageRoot, { childList: true, subtree: true });
(window as any).__toggleFormula = function(btn: HTMLElement, e?: Event): void {
    if (e) e.stopPropagation();
    const row = btn.closest('[data-map-row]') as HTMLElement | null;
    const sel = row?.querySelector<HTMLSelectElement>('.map-source');
    if (!sel) return;
    const wrap = sel.closest<HTMLElement>('.map-source-wrap');
    if (sel.value === '__formula__') {
        sel.value = btn.dataset.lastSourceValue || '';
        wrap?.classList.remove('is-formula-mode');
    } else {
        btn.dataset.lastSourceValue = sel.value;
        sel.value = '__formula__';
        wrap?.classList.add('is-formula-mode');
    }
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    // Keep the custom source trigger synchronized even if the mapping row was
    // enhanced before the cascade control attached its own change listener.
    if (wrap) {
        const trigger = wrap.querySelector<HTMLButtonElement>('.cascade-trigger');
        if (trigger && sel.value !== '__formula__') {
            const option = sel.options[sel.selectedIndex];
            trigger.innerHTML = `<span class="cascade-value">${sel.value ? escapeHtml(option?.textContent?.trim() || '') : '— 不自动抓取 —'}</span>`;
        }
    }
};
function renderSetup(root: HTMLElement, auth: AuthSnapshot, editing: Plan | null, access: {financeOk?: boolean; litigationOk?: boolean; fromLitigationList?: boolean} = {}) {
    activePlanAuth = auth;
    const draft = draftFrom(auth, editing, access);
    const pageTitle = editing ? '编辑台账方案' : '新建台账方案';
    const typeOptions = [
        canManageCustomsPlans() ? `<option value="customs-invoice-matching" ${draft.ledgerType === 'customs-invoice-matching' ? 'selected' : ''}>报关单及发票匹配台账</option>` : '',
        canManageInvoicePlans() ? `<option value="invoice-ledger" ${draft.ledgerType === 'invoice-ledger' ? 'selected' : ''}>发票台账</option>` : '',
        canManageLitigationPlans() ? `<option value="legal-litigation" ${draft.ledgerType === 'legal-litigation' ? 'selected' : ''}>诉讼台账</option>` : '',
    ].filter(Boolean).join('');
    const typeField = `<label class="plans-field"><span>台账类型<span class="req">*</span></span><select class="tp-select" name="ledgerType">${typeOptions}</select></label>`;
    renderLayout(root, auth, `<div class="plans-page plans-workflow-page">
  <header class="plans-workflow-head">
    <div class="tp-page-head-main">
      <p class="tp-eyebrow">方案配置</p>
      <h1 class="tp-title" id="planWorkflowTitle">${pageTitle}</h1>
      <p class="tp-hero-desc" id="planWorkflowDesc">填写方案名称与台账类型，完成后进入模板上传</p>
      <div class="plans-workflow-steps" aria-label="配置步骤">
        <span class="plans-workflow-step is-active" data-step="setup">1. 基本信息</span>
        <span class="plans-workflow-step" data-step="upload">2. 上传模板</span>
      </div>
    </div>
    <div class="plans-workflow-head-actions">
      <button class="tp-btn-secondary" type="button" id="planWorkflowBack" hidden>上一步</button>
      <button class="tp-btn-secondary" type="button" id="backPlanList">返回方案列表</button>
    </div>
  </header>

  <section class="plans-workflow-section template-plan-setup" id="planSetupSection">
    <h2 class="plans-workflow-section-title">方案基本信息</h2>
    <form id="templateSetupForm" class="plans-workflow-form template-plan-setup-body" data-edit-mode="${editing ? '1' : '0'}">
      <label class="plans-field">
        <span>台账方案名称<span class="req">*</span></span>
        <input class="tp-input" name="name" value="${escapeHtml(draft.name)}" placeholder="请输入台账方案名称" required maxlength="120">
      </label>
      ${typeField}
      ${canUseTeamLedgerPlan(auth) ? `<fieldset class="plans-field plans-scope-field">
        <legend>保存位置</legend>
        <label class="plans-scope-option"><input type="radio" name="planScope" value="personal" ${draft.planScope !== 'enterprise' ? 'checked' : ''}><span><b>个人方案</b><em>写入个人空间「台账方案」文件夹</em></span></label>
        <label class="plans-scope-option"><input type="radio" name="planScope" value="enterprise" ${draft.planScope === 'enterprise' ? 'checked' : ''}><span><b>团队方案</b><em>写入当前团队空间，供团队成员共用</em></span></label>
      </fieldset>` : '<input type="hidden" name="planScope" value="personal">'}
      <input type="hidden" name="companyName" value="${escapeHtml(auth.organization.name)}">
      <input type="hidden" name="recognitionModuleId" value="${escapeHtml(draft.recognitionModuleId)}">
    </form>
  </section>

  <section class="plans-workflow-section template-upload-surface" id="templateUploadSurface" hidden>
    <div class="plans-workflow-stepmeta template-upload-head">
      <span class="plans-status-pill is-ready" id="uploadStepStatus">可以上传</span>
    </div>
    <div class="plans-workflow-upload template-upload-body">
      <div class="plans-upload-bar upload-bar" id="templateDropZone">
        <div class="plans-upload-bar-icon upload-bar-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M11 3v10M7 7l4-4 4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path d="M4 14.5v2.8A1.7 1.7 0 0 0 5.7 19h10.6a1.7 1.7 0 0 0 1.7-1.7v-2.8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
        </div>
        <div class="plans-upload-bar-copy upload-bar-text">
          <div class="upload-bar-title" id="templateUploadTitle">上传台账模版文件</div>
          <div class="upload-bar-sub">支持 .xlsx / .xls / .xlsm，单文件 ≤ 50MB，原样式与公式保留</div>
        </div>
        <button type="button" class="tp-btn-primary" id="chooseTemplateFile">上传台账</button>
        <input id="templateInput" type="file" accept=".xlsx,.xls,.xlsm" hidden>
      </div>
      <div class="recognition-progress-track template-upload-progress"><div class="recognition-progress-fill" id="templateUploadProgress"></div></div>
      <p class="plans-upload-note template-locked-note" id="templateUploadLockedNote" hidden>请先在上一步填写台账方案名称</p>
    </div>
  </section>
  <div class="plans-form-msg" id="planMessage"></div>
</div>`); root.querySelector('#backPlanList')?.addEventListener('click', () => { window.history.replaceState({}, '', setupReturnHref || '/tax-assistant/plans'); void renderPlanPage(root, auth); }); const form = root.querySelector<HTMLFormElement>('#templateSetupForm'); const input = root.querySelector<HTMLInputElement>('#templateInput'); const zone = root.querySelector<HTMLElement>('#templateDropZone'); const message = root.querySelector<HTMLElement>('#planMessage'); const ready = () => Boolean((form?.elements.namedItem('name') as HTMLInputElement)?.value.trim()); const sync = () => { const isReady = ready(); zone?.classList.toggle('locked', !isReady); const status = root.querySelector<HTMLElement>('#uploadStepStatus'); if (status) {
    status.textContent = isReady ? '可以上传' : '等待填写';
    status.className = `plans-status-pill ${isReady ? 'is-ready' : 'is-warn'}`;
} const note = root.querySelector<HTMLElement>('#templateUploadLockedNote'); if (note)
    note.hidden = isReady; const count = root.querySelectorAll('input[name="capability"]:checked').length; const summary = root.querySelector<HTMLElement>('#capabilitySummary'); if (summary)
    summary.textContent = `已选择 ${count} 个模块`; }; form?.addEventListener('input', sync); sync(); const choose = () => { if (!ready()) {
    if (message)
        message.textContent = '请先填写台账方案名称';
    return;
} input?.click(); }; root.querySelector('#chooseTemplateFile')?.addEventListener('click', choose); zone?.addEventListener('click', event => { if (!(event.target instanceof HTMLButtonElement))
    choose(); }); zone?.addEventListener('dragover', event => { event.preventDefault(); if (ready())
    zone.classList.add('drag'); }); zone?.addEventListener('dragleave', () => zone.classList.remove('drag')); zone?.addEventListener('drop', event => { event.preventDefault(); zone.classList.remove('drag'); const file = event.dataTransfer?.files?.[0]; if (file)
    void uploadAndInspect(file); }); input?.addEventListener('change', () => { const file = input.files?.[0]; if (file)
    void uploadAndInspect(file); input.value = ''; }); async function uploadAndInspect(file: File) { if (!ready() || !form)
    return; if (!/\.(xlsx|xls|xlsm)$/i.test(file.name)) {
    if (message)
        message.textContent = '仅支持 .xlsx、.xls、.xlsm 台账文件';
    return;
} if (file.size > 50 * 1024 * 1024) {
    if (message)
        message.textContent = '台账模板不能超过 50MB';
    return;
} const progress = root.querySelector<HTMLElement>('#templateUploadProgress'); const title = root.querySelector<HTMLElement>('#templateUploadTitle'); try {
    zone?.classList.add('busy');
    if (title)
        title.textContent = `正在上传：${file.name}`;
    if (progress)
        progress.style.width = '28%';
    if (message)
        message.textContent = '正在上传企业台账模板…';
    const data = new FormData(form);
    const ledgerType = String(data.get('ledgerType') || activePlanDraft?.ledgerType || 'customs-invoice-matching');
    const invoiceLedger = ledgerType === 'invoice-ledger';
    const planScope = canUseTeamLedgerPlan(auth) && String(data.get('planScope') || '') === 'enterprise' ? 'enterprise' : 'personal';
    const editorDraft = {
        name: String(data.get('name') || ''),
        companyName: String(data.get('companyName') || auth.organization.name),
        ledgerType,
        planScope,
        recognitionModuleId: invoiceLedger
            ? capabilities.find(item => item.id === 'invoice-unified-v1')?.id || 'invoice-unified-v1'
            : activePlanDraft?.recognitionModuleId || String(data.get('recognitionModuleId') || defaultRecognitionModuleId),
        capabilityModuleIds: invoiceLedger
            ? ['invoice-unified-v1', 'common-field-processing-v1', 'ledger-export-v1']
            : (activePlanDraft?.capabilityModuleIds?.length
                ? activePlanDraft.capabilityModuleIds
                : [...new Set([activePlanDraft?.recognitionModuleId || defaultRecognitionModuleId, ...capabilities.filter(item => item.required || item.defaultSelected).map(item => item.id)])]),
        moduleVersions: activePlanDraft?.moduleVersions ?? Object.fromEntries(capabilities.map(item => [item.id, item.version])),
    };
    activePlanDraft = editorDraft;
    const created = await api.post<{
        file: {
            id: string;
        };
        uploadUrl: string;
    }>(apiUrl('/api/files/upload-url'), { fileName: file.name, sizeBytes: file.size, mimeType: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', kind: 'template', planScope: editorDraft.planScope });
    await api.upload(apiUrl(`/api/files/${encodeURIComponent(created.file.id)}/content`), file, file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
    if (progress)
        progress.style.width = '65%';
    await api.post(apiUrl(`/api/files/${encodeURIComponent(created.file.id)}/complete`));
    if (message)
        message.textContent = '正在识别工作表、表头和字段…';
    const inspected = await inspectPlanFile(created.file.id, editorDraft);
    if (progress)
        progress.style.width = '100%';
    renderEditor(root, auth, created.file.id, inspected, editorDraft, editing?.id || null, undefined, editing?.fields || []);
}
catch (error) {
    zone?.classList.remove('busy');
    if (progress)
        progress.style.width = '0';
    if (title)
        title.textContent = '点击或拖入企业现有台账';
    if (message)
        message.textContent = error instanceof Error ? error.message : String(error);
} } }
async function editPlan(root: HTMLElement, auth: AuthSnapshot, plan: Plan) {
    if (isLitigationListPlan(plan)) {
        navigate(litigationPlanEditorHref({planId: plan.id, name: plan.name}));
        return;
    }
    if (!plan.templateFileId) {
    alert('该历史方案缺少模板文件引用，暂时只能复制或删除。');
    return;
}
    try {
    const draft = draftFrom(auth, plan);
    const inspected = await inspectPlanFile(plan.templateFileId, draft);
    if (!isLitigationLedgerPlan(draft) && plan.sheetName) {
        ledgerSheetStructureDraft.set(plan.sheetName, {
            headerRow: plan.headerRow || 1,
            dataStartRow: plan.dataStartRow || (plan.headerRow || 1) + 1,
        });
    }
    renderEditor(root, auth, plan.templateFileId, inspected, draft, plan.id, plan.sheetName, plan.fields || []);
}
catch (error) {
    alert(error instanceof Error ? error.message : String(error));
} }
function buildCascadeFromSelect(select: HTMLSelectElement): { groups: Array<{ label: string; items: Array<{ value: string; label: string; meta: string }> }>, topItems: Array<{ value: string; label: string; meta: string }> } {
    const groups: Array<{ label: string; items: Array<{ value: string; label: string; meta: string }> }> = [];
    const topItems: Array<{ value: string; label: string; meta: string }> = [];
    const children = Array.from(select.children);
    children.forEach(child => {
        if (child.tagName === 'OPTION') {
            const opt = child as HTMLOptionElement;
            if (opt.value === '' || opt.value === '__formula__') return;
            topItems.push({ value: opt.value, label: opt.textContent || opt.value, meta: '' });
        } else if (child.tagName === 'OPTGROUP') {
            const group = child as HTMLOptGroupElement;
            const opts = Array.from(group.querySelectorAll('option'));
            const items = opts.map(opt => {
                const text = opt.textContent || opt.value;
                const parts = text.split('｜');
                return { value: opt.value, label: parts.length > 1 ? parts.slice(1).join('｜').trim() : text, meta: parts[0]?.trim() || '' };
            });
            if (items.length) groups.push({ label: group.label, items });
        }
    });
    return { groups, topItems };
}
function initCascadeSelect(select: HTMLSelectElement): void {
    if (select.parentElement?.classList.contains('map-source-wrap')) {
        select.classList.add('is-cascade-native-source');
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');
        select.style.display = 'none';
        return;
    }
    const wrap = document.createElement('div');
    wrap.className = 'map-source-wrap';
    select.parentNode?.insertBefore(wrap, select);
    wrap.appendChild(select);
    // The native select must never remain interactive: it opens an OS/browser
    // dropdown inside the mapping row and can cover the rows below. The
    // accessible value is still kept in the select for form submission; the
    // visible control is the custom cascade trigger below.
    select.tabIndex = -1;
    select.classList.add('is-cascade-native-source');
    select.setAttribute('aria-hidden', 'true');
    select.style.display = 'none';
    const row = select.closest('[data-map-row]') as HTMLElement | null;
    const formulaBtn = row?.querySelector<HTMLButtonElement>('.mapping-source-tools .formula-toggle') || null;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cascade-trigger';
    const updateTrigger = () => {
        const opt = select.options[select.selectedIndex];
        const text = opt?.textContent?.trim() || '';
        const isFormula = select.value === '__formula__';
        const isLitRef = select.value.startsWith(LITIGATION_REF_PREFIX);
        if (formulaBtn) formulaBtn.classList.toggle('is-active', isFormula);
        wrap.classList.toggle('is-formula-mode', isFormula);
        if (!select.value || isFormula) {
            trigger.innerHTML = `<span class="cascade-value">${isFormula ? '自定义计算规则' : '— 不自动抓取 —'}</span>`;
        } else if (isLitRef) {
            trigger.innerHTML = `<span class="cascade-value">${escapeHtml(litigationReferenceOptionLabel(select.value))}</span>`;
        } else {
            trigger.innerHTML = `<span class="cascade-value">${escapeHtml(text)}</span>`;
        }
    };
    updateTrigger();
    wrap.appendChild(trigger);
    const panel = document.createElement('div');
    panel.className = isLitigationLedgerPlan() ? 'cascade-panel ll-fill-cascade' : 'cascade-panel';
    document.body.appendChild(panel);
    const fieldTip = ensureLitigationFieldTip();
    const hideFieldTip = () => {
        fieldTip.hidden = true;
        fieldTip.innerHTML = '';
        fieldTip.style.left = '';
        fieldTip.style.top = '';
    };
    /**
     * 在当前字段右侧弹出填写规则。
     * @param item 当前悬停的级联项
     */
    const showFieldTip = (item: HTMLElement) => {
        if (!isLitigationLedgerPlan()) {
            hideFieldTip();
            return;
        }
        const hint = litigationCascadeHint(item.dataset.value || '');
        if (!hint) {
            hideFieldTip();
            return;
        }
        fieldTip.innerHTML = `<strong>填写规则</strong><p>${escapeHtml(hint)}</p>`;
        fieldTip.hidden = false;
        const itemRect = item.getBoundingClientRect();
        const gap = 8;
        const maxTip = Math.min(320, window.innerWidth - 24);
        let width = maxTip;
        let left = itemRect.right + gap;
        if (left + width > window.innerWidth - 12) {
            const fitRight = window.innerWidth - 12 - left;
            if (fitRight >= 180) {
                width = fitRight;
            } else {
                width = Math.min(maxTip, Math.max(160, itemRect.left - 12 - gap));
                left = itemRect.left - gap - width;
                if (left < 12) {
                    left = 12;
                    width = Math.min(maxTip, window.innerWidth - 24);
                }
            }
        }
        fieldTip.style.width = `${width}px`;
        const tipH = fieldTip.offsetHeight || 80;
        let top = itemRect.top;
        if (top + tipH > window.innerHeight - 12) top = Math.max(12, window.innerHeight - 12 - tipH);
        if (top < 12) top = 12;
        fieldTip.style.left = `${left}px`;
        fieldTip.style.top = `${top}px`;
    };
    panel.addEventListener('mouseover', (event) => {
        const target = event.target as HTMLElement | null;
        if (target?.closest?.('.cascade-cat')) {
            hideFieldTip();
            return;
        }
        const item = target?.closest?.('.cascade-item') as HTMLElement | null;
        if (!item || !panel.contains(item)) return;
        showFieldTip(item);
    });
    let activeGroup = '';
    let activeReferenceSheet = '';
    const litigationRowScopedGroups = new Set(['用户手动填写', '用户选择']);
    const renderPanel = () => {
        hideFieldTip();
        let { groups, topItems } = buildCascadeFromSelect(select);
        const currentSheetName = document.querySelector<HTMLSelectElement>('#sheetSelect')?.value || activeInspection?.activeSheetName || '';
        const refSheets = isLitigationLedgerPlan() ? litigationReferenceSheetOptions(activeInspection, currentSheetName) : [];
        if (isLitigationLedgerPlan() && refSheets.length && !groups.some((group) => group.label === LITIGATION_REF_GROUP)) {
            groups = [...groups, {label: LITIGATION_REF_GROUP, items: []}];
        }
        if (!activeGroup || !groups.some((group) => group.label === activeGroup)) activeGroup = groups[0]?.label || '';
        if (activeGroup !== LITIGATION_REF_GROUP) activeReferenceSheet = '';
        const hideTopItems = activeGroup === '用户自定义规则' || activeGroup === LITIGATION_REF_GROUP;
        const topHtml = !hideTopItems && topItems.length ? `<div class="cascade-top-item">${topItems.map(item => `<div class="cascade-item${select.value === item.value ? ' is-selected' : ''}" data-value="${escapeHtml(item.value)}"><span class="cascade-item-label">${escapeHtml(item.label)}</span></div>`).join('')}</div>` : '';
        const scopedRowKey = isLitigationLedgerPlan() && litigationRowScopedGroups.has(activeGroup)
            ? litigationRowScopedFieldKeyForGroup(row, activeGroup)
            : '';
        let activeItems = groups.find(g => g.label === activeGroup)?.items || [];
        if (scopedRowKey) activeItems = activeItems.filter((item) => item.value === scopedRowKey);
        const hideScopedItemList = Boolean(scopedRowKey && litigationRowScopedGroups.has(activeGroup));
        let itemsHtml = '';
        if (hideScopedItemList) {
            itemsHtml = '';
        } else if (activeGroup === LITIGATION_REF_GROUP && isLitigationLedgerPlan()) {
            if (!activeReferenceSheet) {
                itemsHtml = refSheets.length
                    ? refSheets.map((sheet) => {
                        const caption = litigationSheetKindCaption(sheetKindOf(sheet));
                        return `<div class="cascade-item cascade-ref-sheet${activeReferenceSheet === sheet.name ? ' is-selected' : ''}" data-ref-sheet="${escapeHtml(sheet.name)}"><span class="cascade-item-label">${escapeHtml(sheet.name)}</span><span class="cascade-item-meta">${escapeHtml(caption)}</span></div>`;
                    }).join('')
                    : '<div class="cascade-empty">暂无其他工作表</div>';
            } else {
                const refKind = detectLitigationSheetKind(activeReferenceSheet);
                const refFields = litigationReferableFieldsForSheetKind(refKind);
                itemsHtml = refFields.length
                    ? refFields.map((field) => {
                        const value = encodeLitigationSheetRef(activeReferenceSheet, field.id);
                        return `<div class="cascade-item${select.value === value ? ' is-selected' : ''}" data-value="${escapeHtml(value)}"><span class="cascade-item-label">${escapeHtml(field.label)}</span><span class="cascade-item-meta">${escapeHtml(activeReferenceSheet)}</span></div>`;
                    }).join('')
                    : '<div class="cascade-empty">该表暂无可引用字段</div>';
            }
        } else {
            itemsHtml = activeItems.length ? activeItems.map(item => {
                const isAction = item.value === EDIT_INTERNAL_NO_RULE_VALUE;
                const shortRule = isAction ? '点击后编辑编号规则' : '';
                return `<div class="cascade-item${select.value === item.value ? ' is-selected' : ''}${isAction ? ' is-action' : ''}" data-value="${escapeHtml(item.value)}"><span class="cascade-item-label">${escapeHtml(item.label)}</span>${shortRule ? `<span class="cascade-item-meta">${escapeHtml(shortRule)}</span>` : ''}</div>`;
            }).join('') : '<div class="cascade-empty">该分类暂无字段</div>';
        }
        panel.innerHTML = `<div class="cascade-categories">${groups.map(g => {
            const scopedCount = isLitigationLedgerPlan() && litigationRowScopedGroups.has(g.label)
                ? litigationRowScopedFieldKeyForGroup(row, g.label)
                : '';
            let count = scopedCount ? 1 : g.items.length;
            if (g.label === LITIGATION_REF_GROUP) count = refSheets.length;
            return `<div class="cascade-cat${g.label === activeGroup ? ' is-active' : ''}${scopedCount ? ' is-row-scoped' : ''}" data-group="${escapeHtml(g.label)}"><span>${escapeHtml(g.label)}</span><span class="cascade-cat-count">${count}</span></div>`;
        }).join('')}</div><div class="cascade-items${hideScopedItemList ? ' is-row-scoped-hint' : ''}">${activeGroup === LITIGATION_REF_GROUP && activeReferenceSheet ? `<div class="cascade-ref-back" data-ref-back="1">← ${escapeHtml(activeReferenceSheet)}</div>` : ''}${topHtml}${itemsHtml}</div>`;
        panel.classList.toggle('is-litigation-row-scoped', hideScopedItemList);
        panel.classList.toggle('is-litigation-ref-mode', activeGroup === LITIGATION_REF_GROUP);
        const itemsBox = panel.querySelector<HTMLElement>('.cascade-items');
        panel.querySelector('.cascade-ref-back')?.addEventListener('click', () => {
            activeReferenceSheet = '';
            renderPanel();
        });
        panel.querySelectorAll('.cascade-ref-sheet').forEach((item) => {
            item.addEventListener('click', () => {
                activeReferenceSheet = (item as HTMLElement).dataset.refSheet || '';
                renderPanel();
            });
        });
        panel.querySelectorAll('.cascade-item[data-value]').forEach(item => {
            item.addEventListener('click', () => {
                const value = (item as HTMLElement).dataset.value || '';
                if (value === EDIT_INTERNAL_NO_RULE_VALUE) {
                    closePanel();
                    openInternalNoRuleDialog(() => {
                        const key = fieldKey(customInternalNoField());
                        if ([...select.options].some((option) => option.value === key)) {
                            select.value = key;
                            updateTrigger();
                            select.dispatchEvent(new Event('change', {bubbles: true}));
                        }
                    });
                    return;
                }
                if (![...select.options].some((option) => option.value === value)) {
                    select.add(new Option(litigationReferenceOptionLabel(value), value));
                }
                select.value = value;
                updateTrigger();
                closePanel();
                select.dispatchEvent(new Event('change', { bubbles: true }));
            });
        });
        itemsBox?.addEventListener('mouseleave', hideFieldTip);
        itemsBox?.addEventListener('scroll', hideFieldTip);
    };
    const openPanel = () => {
        if (select.value === '__formula__') return;
        const { groups } = buildCascadeFromSelect(select);
        let foundGroup = '';
        for (const g of groups) {
            if (g.items.some(it => it.value === select.value)) { foundGroup = g.label; break; }
        }
        if (foundGroup) activeGroup = foundGroup;
        else if (groups.length) activeGroup = groups[0].label;
        renderPanel();
        const winW = window.innerWidth;
        const winH = window.innerHeight;
        const fillCascade = panel.classList.contains('ll-fill-cascade');
        const panelW = winW < 600 ? Math.min(winW - 24, 340) : 184;
        const submenuW = winW < 600 ? panelW : (fillCascade ? 360 : 282);
        const submenuGap = 6;
        const panelMaxH = winH < 600 ? 300 : (fillCascade ? 420 : 360);
        const triggerRect = trigger.getBoundingClientRect();
        panel.classList.add('is-open');
        trigger.classList.add('is-open');
        const openSubmenuLeft = winW >= 600 && triggerRect.left + panelW + submenuGap + submenuW > winW - 12;
        panel.classList.toggle('is-submenu-left', openSubmenuLeft);
        panel.style.position = 'fixed';
        panel.style.zIndex = '9999';
        panel.style.width = panelW + 'px';
        const availableBelow = Math.max(120, Math.min(panelMaxH, winH - triggerRect.bottom - 18));
        panel.style.maxHeight = availableBelow + 'px';
        let left = triggerRect.left;
        const top = triggerRect.bottom + 6;
        if (left + panelW > winW - 12) left = winW - panelW - 12;
        if (left < 12) left = 12;
        const categoriesBox = panel.querySelector<HTMLElement>('.cascade-categories');
        const itemsBox = panel.querySelector<HTMLElement>('.cascade-items');
        if (categoriesBox) categoriesBox.style.maxHeight = availableBelow + 'px';
        if (itemsBox) itemsBox.style.maxHeight = availableBelow + 'px';
        panel.style.left = left + 'px';
        panel.style.top = top + 'px';
        panel.classList.remove('is-right-aligned');
        const selected = panel.querySelector('.cascade-item.is-selected') as HTMLElement | null;
        if (selected) {
            if (itemsBox) itemsBox.scrollTop = selected.offsetTop - 80;
        }
    };
    const closePanel = () => {
        hideFieldTip();
        panel.classList.remove('is-open');
        trigger.classList.remove('is-open');
        panel.style.position = '';
        panel.style.left = '';
        panel.style.top = '';
        panel.style.zIndex = '';
        panel.style.width = '';
        panel.style.maxHeight = '';
        panel.querySelectorAll<HTMLElement>('.cascade-categories,.cascade-items').forEach(box => { box.style.maxHeight = ''; });
        panel.classList.remove('is-submenu-left');
        panel.classList.remove('is-litigation-row-scoped');
    };
    if (!panel.dataset.llCascadeBound) {
        panel.dataset.llCascadeBound = '1';
        /**
         * 诉讼方案：手填/选择类一级分类点击即绑定本列并关闭面板。
         * @param group 一级分类名
         */
        const applyLitigationScopedCategory = (group: string) => {
            if (!isLitigationLedgerPlan() || !litigationRowScopedGroups.has(group)) return false;
            activeGroup = group;
            const scoped = litigationRowScopedFieldKeyForGroup(row, group);
            if (scoped && [...select.options].some((option) => option.value === scoped)) {
                select.value = scoped;
                updateTrigger();
                select.dispatchEvent(new Event('change', { bubbles: true }));
            }
            closePanel();
            return true;
        };
        panel.addEventListener('mouseover', (event) => {
            const cat = (event.target as HTMLElement).closest('.cascade-cat');
            if (!cat || !panel.contains(cat)) return;
            const group = cat.dataset.group || '';
            if (!group || group === activeGroup) return;
            activeGroup = group;
            renderPanel();
        });
        panel.addEventListener('mousedown', (event) => {
            const cat = (event.target as HTMLElement).closest('.cascade-cat');
            if (!cat || !panel.contains(cat) || !panel.classList.contains('is-open')) return;
            const group = cat.dataset.group || '';
            if (!applyLitigationScopedCategory(group)) return;
            event.preventDefault();
            event.stopPropagation();
        });
    }
    trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        if (panel.classList.contains('is-open')) closePanel();
        else {
            document.querySelectorAll('.cascade-panel.is-open').forEach(p => { p.classList.remove('is-open'); (p.previousElementSibling as HTMLElement)?.classList.remove('is-open'); });
            openPanel();
        }
    });
    document.addEventListener('click', (e) => {
        if (!wrap.contains(e.target as Node) && !panel.contains(e.target as Node)) closePanel();
    });
    const closeOnScroll = (event: Event) => {
        // Opening the panel scrolls its own item list to the current value. That
        // internal scroll must not immediately close the panel again.
        const target = event.target;
        if (target instanceof Node && panel.contains(target)) return;
        if (panel.classList.contains('is-open')) closePanel();
    };
    document.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    select.addEventListener('change', () => {
        if (select.value !== '__formula__') lastSourceValue = select.value;
        updateTrigger();
    });
}
function initCascadeSelects(root: HTMLElement): void {
    root.querySelectorAll<HTMLSelectElement>('.mapping-table .map-source').forEach(select => initCascadeSelect(select));
}
/**
 * 诉讼字段填写规则气泡，全页共用一个，避免重复创建。
 */
function ensureLitigationFieldTip(): HTMLElement {
    const existing = document.getElementById('llCascadeFieldTip');
    if (existing) return existing;
    const tip = document.createElement('div');
    tip.id = 'llCascadeFieldTip';
    tip.className = 'cascade-field-tip';
    tip.hidden = true;
    document.body.appendChild(tip);
    return tip;
}
/**
 * 诉讼方案映射表：按系统标准字段目录一行一字段（与后台维护的台账字段一致）。
 */
function litigationEditorMappingTableBody(
    sheet: Sheet,
    draft: Draft,
    inspection: Inspection,
    resolvedFields: PlanField[],
): string {
    const sheetKind = sheetKindOf(sheet);
    const spec = LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === sheetKind);
    const fieldIds = spec?.fieldIds ?? [];
    const mappingFields = planMappingFields(inspection, draft, sheet.name);
    const bySource = new Map(resolvedFields.map((field) => [field.sourceField, field]));
    return fieldIds.map((fieldId, index) => {
        const existing = bySource.get(fieldId);
        const column = sheet.columns.find((col) => suggestLitigationPlanField(col.header, sheetKind) === fieldId)
            ?? (existing?.columnLetter ? sheet.columns.find((col) => col.columnLetter === existing.columnLetter) : undefined);
        const columnIndex = column?.columnIndex ?? existing?.columnIndex ?? index;
        const columnLetter = column?.columnLetter ?? existing?.columnLetter ?? '';
        const standardLabel = litigationPlanFieldLabel(fieldId);
        const excelHeader = column?.header?.trim() || '';
        const pseudoColumn: Column = column ?? {
            columnIndex,
            columnLetter,
            header: excelHeader || standardLabel,
            suggestedField: fieldId,
            suggestedScore: column ? 100 : 0,
        };
        let source = existing ? mappingFieldKey(existing, mappingFields) : litigationFieldKeyForId(fieldId, mappingFields);
        if (!source) source = litigationMappingSelectValue(pseudoColumn, mappingFields, existing, sheetKind);
        const enabled = existing ? existing.enabled : Boolean(source);
        const matchHint = column
            ? (suggestLitigationPlanField(column.header, sheetKind) === fieldId ? `已对应模板列「${excelHeader}」` : `模板列「${excelHeader}」`)
            : '标准字段（请使用最新系统四表模板以补齐 Excel 列）';
        return `<tr data-map-row="${index}" data-standard-field-id="${escapeHtml(fieldId)}" data-column-index="${columnIndex}" data-column-letter="${escapeHtml(columnLetter)}" data-header="${escapeHtml(standardLabel)}" data-suggested-field="${escapeHtml(fieldId)}"><td><input class="map-enabled" type="checkbox" ${enabled ? 'checked' : ''}></td><td><span class="mapping-column-letter">${columnLetter ? escapeHtml(columnLetter) : '—'}</span>${excelHeader && excelHeader !== standardLabel ? `<small class="mapping-excel-header-hint">${escapeHtml(excelHeader)}</small>` : ''}</td><td><b class="mapping-enterprise-field">${escapeHtml(standardLabel)}</b></td><td><select class="map-source" aria-label="${escapeHtml(standardLabel)}对应的数据来源与标准字段">${groupedFieldOptions(mappingFields, source, true, draft)}</select></td><td><span class="mapping-confidence ${column ? 'high' : 'low'}">${escapeHtml(matchHint)}</span></td></tr>`;
    }).join('');
}

/**
 * 方案编辑页「工作表 / 表头行」区域：诉讼台账仅切换四表，行号由系统模板固定。
 */
function renderPlanEditorStructureGrid(
    inspection: Inspection,
    sheet: Sheet,
    draft: Draft,
    headerRow: number,
    dataStartRow: number,
): string {
    const sheetOptions = inspection.sheets.map((item) => `<option value="${escapeHtml(item.name)}" ${item.name === sheet.name ? 'selected' : ''}>${escapeHtml(item.name)}（${isLitigationLedgerPlan(draft) ? `${escapeHtml(litigationSheetKindCaption(sheetKindOf(item)))} · ` : ''}${item.rowCount} 行）</option>`).join('');
    if (isLitigationLedgerPlan(draft)) {
        const fixed = fixedLitigationSheetStructure();
        return `<div class="template-structure-grid template-structure-grid--litigation"><label class="template-structure-sheet">配置工作表<span class="req">*</span><select class="form-select" id="sheetSelect" name="sheetName">${sheetOptions}</select></label><p class="template-structure-platform-note panel-note">表头与数据起始行已在管理后台维护的系统标准四表模板中固定（表头第 ${fixed.headerRow} 行，数据自第 ${fixed.dataStartRow} 行起），此处只需切换工作表并配置字段映射。</p></div>`;
    }
    return `<div class="template-structure-grid"><label>工作表（共 ${inspection.sheets.length} 个表）<span class="req">*</span><select class="form-select" id="sheetSelect" name="sheetName">${sheetOptions}</select></label><label>表头所在行 / 数据起始行<span class="req">*</span><div class="template-structure-row"><input class="form-input" name="headerRow" type="number" min="1" value="${headerRow}"><input class="form-input" name="dataStartRow" type="number" min="2" value="${dataStartRow}"></div></label></div>`;
}

function renderEditor(root: HTMLElement, auth: AuthSnapshot, fileId: string, inspection: Inspection, draft: Draft, editingId: string | null, selectedSheetName?: string, existingFields: PlanField[] = [], editorStatusMessage = '') { document.getElementById('llInternalNoRuleDialog')?.remove(); activePlanDraft = draft; activeMappingFields = existingFields; activeInspection = inspection; const sheet = inspection.sheets.find(item => item.name === selectedSheetName) || bestInspectionSheet(inspection.sheets, inspection.activeSheetName); if (!sheet) {
    renderLayout(root, auth, '<div class="plans-error">模板中没有可读取的工作表</div>');
    return;
} if (isLitigationLedgerPlan(draft)) seedLitigationSheetMapsFromInspection(inspection, draft); const resolvedFields = existingFields.length ? existingFields : (litigationSheetMaps.get(sheet.name)?.exportFields || []); if (!existingFields.length) activeMappingFields = resolvedFields; const existingByColumnLetter = new Map(resolvedFields.map(field => [String(field.columnLetter || ''), field])); const mappingFields = planMappingFields(inspection, draft, sheet.name); const mappingGuideSummary = isLitigationLedgerPlan(draft) ? `当前工作表按「${litigationSheetKindCaption(sheetKindOf(sheet))}」字段展示。内部案号可从诉讼台账、用户手动填写、系统生成或用户自定义规则取值。` : isInvoiceLedgerPlan(draft) ? '下拉框按发票和系统生成分组展示' : '下拉框按报关单、退税率库、发票和系统生成四类分组展示'; const savedLitigationSheet = litigationSheetMaps.get(sheet.name); const savedLedgerStructure = ledgerSheetStructureDraft.get(sheet.name); const fixedLitigationStructure = fixedLitigationSheetStructure(); const headerRow = isLitigationLedgerPlan(draft) ? fixedLitigationStructure.headerRow : (savedLedgerStructure?.headerRow ?? (editingId && selectedSheetName === sheet.name && draft.headerRow ? draft.headerRow : sheet.suggestedHeaderRow)); const dataStartRow = isLitigationLedgerPlan(draft) ? fixedLitigationStructure.dataStartRow : (savedLedgerStructure?.dataStartRow ?? (editingId && selectedSheetName === sheet.name && draft.dataStartRow ? draft.dataStartRow : headerRow + 1)); renderLayout(root, auth, `<div class="legacy-module-page plans-workflow-page">${steps(3)}<div class="template-workflow-toolbar"><strong>${isLitigationLedgerPlan(draft) ? '诉讼台账方案编辑' : editingId ? '编辑台账方案' : '新建台账方案'}</strong><button class="secondary-button" id="cancelPlanEdit">返回方案列表</button></div><section class="template-plan-setup plan-summary-surface"><div class="template-plan-setup-head"><div><div class="template-plan-setup-title">方案基本信息</div><div class="template-plan-setup-note">${escapeHtml(draft.name)} · ${escapeHtml(draft.companyName)} · ${escapeHtml(inspection.fileName)}</div></div><span class="status status-green">格式识别完成</span></div></section><form id="planEditor"><section class="admin-panel template-mapping-surface"><div class="panel-heading"><div><h2>确认台账结构和字段映射</h2><p class="panel-note">${isLitigationLedgerPlan(draft) ? '切换标准四表之一并映射该表字段。案件总表存快照；当事人、程序与期限、进展记录为一对多明细，以内部案号关联。' : '模板目标列可映射专业模块字段，也可使用系统内置的连续序号和通用取值处理。'}</p></div><span class="status-tag success">${isLitigationLedgerPlan(draft) ? (LITIGATION_DEFAULT_SHEETS.find((item) => item.kind === sheetKindOf(sheet))?.fieldIds.length || sheet.columns.length) : sheet.columns.length} 个目标字段</span></div>${renderPlanEditorStructureGrid(inspection, sheet, draft, headerRow, dataStartRow)}<div class="mapping-guide"><div><b>选择数据来源</b><span>${mappingGuideSummary}</span></div><div><b>设置通用处理</b><span>专业字段可使用原值，或由通用处理模块截取开头、末尾 N 位等</span></div><div><b>使用系统生成</b><span>连续序号自动生成；复杂计算可使用自定义计算公式</span></div></div><div class="table-scroll"><table class="mapping-table"><thead><tr><th>启用</th><th>Excel列</th><th>台账模板目标字段</th><th>数据来源与标准字段</th><th>匹配建议</th></tr></thead><tbody>${isLitigationLedgerPlan(draft) ? litigationEditorMappingTableBody(sheet, draft, inspection, resolvedFields) : sheet.columns.map((column, index) => { const existing = existingByColumnLetter.get(column.columnLetter); const sheetKind = sheetKindOf(sheet); const source = existing ? mappingFieldKey(existing, mappingFields) : suggestionFieldKey(column, mappingFields); const enabled = existing ? existing.enabled : Boolean(source); return `<tr data-map-row="${index}" data-column-index="${column.columnIndex}" data-column-letter="${escapeHtml(column.columnLetter)}" data-header="${escapeHtml(column.header)}" data-suggested-field="${escapeHtml(suggestionFieldKey(column, mappingFields))}"><td><input class="map-enabled" type="checkbox" ${enabled ? 'checked' : ''}></td><td><span class="mapping-column-letter">${escapeHtml(column.columnLetter)}</span></td><td><b class="mapping-enterprise-field">${escapeHtml(column.header || '未命名列')}</b></td><td><select class="map-source" aria-label="${escapeHtml(column.header||column.columnLetter)}对应的数据来源与标准字段">${groupedFieldOptions(mappingFields,source,true,draft)}</select></td><td><span class="mapping-confidence ${column.suggestedScore >= 80 ? 'high' : column.suggestedScore >= 50 ? 'medium' : 'low'}">${column.suggestedField ? `建议匹配 ${column.suggestedScore || 0}%` : '待人工选择'}</span></td></tr>`; }).join('')}</tbody></table></div><div class="editor-actions"><span class="form-message" id="editorMessage"></span><button class="primary-button" type="submit">${editingId ? '保存方案修改' : '保存台账方案'}</button></div></section></form></div>`); root.querySelector('#cancelPlanEdit')?.addEventListener('click', () => isLitigationLedgerPlan(draft) ? backToLitigationPlans() : void renderPlanPage(root, auth)); root.querySelector<HTMLSelectElement>('#sheetSelect')?.addEventListener('change', event => { const next = (event.currentTarget as HTMLSelectElement).value; if (isLitigationLedgerPlan(draft)) { harvestCurrentLitigationSheet(root, sheet.name); draft.internalNoRule = readInternalNoRuleFromForm(root); } else { harvestCurrentLedgerPlanSheet(root, sheet.name); } renderEditor(root, auth, fileId, inspection, draft, editingId, next, isLitigationLedgerPlan(draft) ? (litigationSheetMaps.get(next)?.exportFields || []) : activeMappingFields); }); root.querySelectorAll<HTMLSelectElement>('.map-source').forEach(select => select.addEventListener('change', () => { const checkbox = select.closest('tr')?.querySelector<HTMLInputElement>('.map-enabled'); if (checkbox)
    checkbox.checked = Boolean(select.value); })); ensureEditorModuleOptions(); enhanceMappingAdvancedControls(root); initCascadeSelects(root); bindStructureRowConfirm(root, auth, fileId, draft, editingId, sheet.name);
    if (editorStatusMessage) {
        const status = root.querySelector<HTMLElement>('#editorMessage');
        if (status) status.textContent = editorStatusMessage;
    }
    root.querySelector<HTMLFormElement>('#planEditor')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    const fields = readEditorMappingRows(root, inspection);
    const message = root.querySelector<HTMLElement>('#editorMessage'); if (!fields.some(field => field.enabled)) {
    if (message)
        message.textContent = '请至少启用一个有效字段映射';
    return;
} const header = isLitigationLedgerPlan(draft) ? fixedLitigationSheetStructure().headerRow : Number(data.get('headerRow')); const start = isLitigationLedgerPlan(draft) ? fixedLitigationSheetStructure().dataStartRow : Number(data.get('dataStartRow')); if (!isLitigationLedgerPlan(draft) && start <= header) {
    if (message)
        message.textContent = '数据起始行必须大于表头所在行';
    return;
} const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]'); if (submit)
    submit.disabled = true; try {
    if (message)
        message.textContent = draft.planScope === 'enterprise' ? '正在保存到当前团队空间…' : '正在保存到个人空间「台账方案」…';
    const payload = isLitigationLedgerPlan(draft)
        ? (() => {
            harvestCurrentLitigationSheet(root, String(data.get('sheetName') || sheet.name));
            const sheetMaps = [...litigationSheetMaps.values()];
            const casesMap = sheetMaps.find((item) => item.sheetKind === 'cases') || sheetMaps[0];
            const casesExportFields = casesMap?.exportFields || fields;
            const serialize = (item: PlanField) => ({
                enabled: item.enabled,
                columnIndex: item.columnIndex,
                columnLetter: item.columnLetter,
                header: item.header,
                sourceField: item.sourceField,
                sourceModuleId: item.sourceModuleId,
                sourceModuleVersion: item.sourceModuleVersion,
                valueMode: item.valueMode,
                valueTransform: item.valueTransform,
                transformLength: item.transformLength,
                transformText: item.transformText,
                calculationExpression: item.calculationExpression,
                calculationDecimalPlaces: item.calculationDecimalPlaces,
                calculationMissingValuePolicy: item.calculationMissingValuePolicy,
                referenceSheetName: item.referenceSheetName,
                referenceColumnIndex: item.referenceColumnIndex,
                referenceColumnLetter: item.referenceColumnLetter,
                referenceHeader: item.referenceHeader,
            });
            return {
                templateFileId: fileId,
                fileName: inspection.fileName,
                name: draft.name,
                companyName: draft.companyName,
                sheetName: casesMap?.sheetName || String(data.get('sheetName') || ''),
                headerRow: casesMap?.headerRow || header,
                dataStartRow: casesMap?.dataStartRow || start,
                listFieldIds: deriveLitigationListFieldIdsFromPlan({exportFields: casesExportFields, sheetMaps}),
                exportFields: casesExportFields.map(serialize),
                sheetMaps: sheetMaps.map((item) => ({
                    sheetKind: item.sheetKind,
                    sheetName: item.sheetName,
                    headerRow: item.headerRow,
                    dataStartRow: item.dataStartRow,
                    exportFields: item.exportFields.map(serialize),
                })),
                internalNoRule: normalizeLitigationInternalNoRule(draft.internalNoRule),
                planScope: draft.planScope === 'enterprise' ? 'enterprise' : 'personal',
            };
        })()
        : { templateFileId: fileId, name: draft.name, companyName: draft.companyName, ledgerType: draft.ledgerType, planScope: draft.planScope === 'enterprise' ? 'enterprise' : 'personal', ...ledgerCapabilityPayload(), sheetName: String(data.get('sheetName') || ''), headerRow: header, dataStartRow: start, fields };
    if (editingId)
        await api.patch(apiUrl(isLitigationLedgerPlan(draft) ? `/api/legal-litigation/plans/${encodeURIComponent(editingId)}` : `/api/ledger/plans/${encodeURIComponent(editingId)}`), payload);
    else
        await api.post(apiUrl(isLitigationLedgerPlan(draft) ? '/api/legal-litigation/plans' : '/api/ledger/plans'), payload);
    if (isLitigationLedgerPlan(draft)) {
        backToLitigationPlans();
        return;
    }
    await renderPlanPage(root, auth);
}
catch (error) {
    if (submit)
        submit.disabled = false;
    if (message)
        message.textContent = error instanceof Error ? error.message : String(error);
} }); }
function renderSaved(root: HTMLElement, auth: AuthSnapshot, name: string) { renderLayout(root, auth, `<div class="legacy-module-page plans-workflow-page">${steps(4)}<div class="template-workflow-toolbar"><strong>台账方案已保存</strong></div><section class="template-save-success"><div class="entity-icon">&#xE73E;</div><h2>${escapeHtml(name)}</h2><p>方案已保存到个人空间「台账方案」文件夹，可以在报关单识别页面选择使用。</p><button class="primary-button" id="backToPlans">返回方案列表</button></section></div>`); root.querySelector('#backToPlans')?.addEventListener('click', () => void renderPlanPage(root, auth)); }
function showPlanSaved(root: HTMLElement, auth: AuthSnapshot, name: string): void {
    const existing = root.querySelector<HTMLElement>('#planSavedNotice');
    existing?.remove();
    const notice = document.createElement('section');
    notice.id = 'planSavedNotice';
    notice.className = 'template-save-success template-save-success-inline';
    notice.innerHTML = `<div><strong>台账方案已保存</strong><span>${escapeHtml(name)} 已保存，可供多个任务选择使用。</span></div><button class="secondary-button" type="button" id="backToPlansInline">返回方案列表</button>`;
    root.querySelector('#planEditor')?.before(notice);
    root.querySelector('#backToPlansInline')?.addEventListener('click', () => void renderPlanPage(root, auth));
    notice.scrollIntoView({behavior:'smooth', block:'start'});
}
